import {
  addDays,
  addMonths,
  differenceInDays,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
} from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import { areaByActiveBySortKey } from "../../../../graphql/reloadyard/custom-queries";
import { rulesByAccountByActiveByEvent } from "../../../../graphql/account/account-queries";
import { updateChargeRule } from "../../../../graphql/mutations";

import {
  CarrierType,
  MAX_ROWS_UPLOAD,
  InboundStatus,
  InventoryStatus,
  ShipmentLineItemStatus,
  Unit,
} from "../../../../utils/constants";
import {
  fetchCarrierEquipmentTypes,
  getConversionValue,
  getUnitName,
} from "../../../../utils/loadTypeUtils";
import { logError, setupLocationMaps } from "../../../../utils/appUtils";
import { chunkArray, trimDecimal } from "../../../../utils/utils";

import { fetchServices, getUnitPrice } from "../../services/ServiceUtils";
import {
  BillingAutomation,
  BillingSource,
  ChargeEvent,
  FeeAccrual,
  FreeDaysPolicy,
  RULE_SCOPE_FIELDS,
  StorageFrequency,
} from "./chargeRuleConstants";

const StorageEntryKind = {
  // Quantity shipped during the billing period (uses shipped date as compare date).
  SHIPPED: "SHIPPED",
  // Current on-hand quantity; used for non-FLAT accruals and day-based billing.
  ONHAND_CURRENT: "ONHAND_CURRENT",
  // On-hand snapshot as of the billing period end date; required for FLAT_ONHAND.
  ONHAND_ASOF_ENDDATE: "ONHAND_ASOF_ENDDATE",
};

export const isPeriodicStorageFrequency = (frequency) =>
  frequency === StorageFrequency.MONTHLY ||
  frequency === StorageFrequency.BIWEEKLY ||
  frequency === StorageFrequency.WEEKLY;

export const ON_PULL_REQUIRES_RAIL =
  "On Pull storage applies to rail cars only. Set Carrier Type to Rail.";

export const isEventDrivenStorageFrequency = (frequency) =>
  frequency === StorageFrequency.ON_RELEASE ||
  frequency === StorageFrequency.ON_PULL ||
  frequency === StorageFrequency.ON_DEPLETION;

export const getPeriodicStorageFrequencies = (rules = []) => [
  ...new Set(
    (rules || [])
      .filter((rule) => rule?.event === ChargeEvent.STORAGE)
      .map((rule) => rule?.frequency)
      .filter(isPeriodicStorageFrequency),
  ),
];

export const isLeasedZone = (zone, accountId) => {
  if (!zone?.accountId || zone?.leasedArea == null || !zone?.leasedAreaUOM) {
    return false;
  }

  if (accountId && zone.accountId !== accountId) return false;
  return true;
};

export const getLeasedZoneMap = (accountId) => {
  const leasedZoneMap = new Map();
  const areas = window.appUser?.reloadYard?.areas?.items || [];

  areas.forEach((area) => {
    const zones = area?.zones?.items || [];
    zones.forEach((zone) => {
      if (isLeasedZone(zone, accountId)) {
        leasedZoneMap.set(zone.id, {
          ...zone,
          areaId: zone?.areaId ?? area?.id ?? null,
          areaName: area?.name ?? "",
        });
      }
    });
  });

  return leasedZoneMap;
};

export const isLeasedZoneId = (
  zoneId,
  accountId,
  leasedZoneMap = getLeasedZoneMap(accountId),
) => {
  if (!zoneId) return false;
  return leasedZoneMap.has(zoneId);
};

export const trimLeasedZoneInventory = (
  inventory,
  accountId,
  leasedZoneMap = getLeasedZoneMap(accountId),
) => {
  if (!inventory || !accountId || !leasedZoneMap.size) return inventory;

  const inventoryItems = inventory?.inventoryItems?.items || [];

  if (!inventoryItems.length) {
    return isLeasedZoneId(inventory?.zoneId, accountId, leasedZoneMap)
      ? null
      : inventory;
  }

  const remainingInventoryItems = inventoryItems.filter((inventoryItem) => {
    const zoneId = inventoryItem?.zoneId ?? inventory?.zoneId;
    return !isLeasedZoneId(zoneId, accountId, leasedZoneMap);
  });

  if (!remainingInventoryItems.length) return null;

  const excludedInventoryItemIds = new Set(
    inventoryItems
      .filter((inventoryItem) => {
        const zoneId = inventoryItem?.zoneId ?? inventory?.zoneId;
        return isLeasedZoneId(zoneId, accountId, leasedZoneMap);
      })
      .map((inventoryItem) => inventoryItem?.id)
      .filter(Boolean),
  );

  const shipmentLineItems = inventory?.shipmentLineItems?.items || [];
  const remainingShipmentLineItems = shipmentLineItems.map(
    (shipmentLineItem) => {
      const shipmentInventoryItems =
        shipmentLineItem?.inventoryItems?.items || [];

      return {
        ...shipmentLineItem,
        inventoryItems: {
          ...shipmentLineItem?.inventoryItems,
          items: shipmentInventoryItems.filter(
            (shipmentInventoryItem) =>
              !excludedInventoryItemIds.has(
                shipmentInventoryItem?.inventoryItemId,
              ),
          ),
        },
      };
    },
  );

  const primaryInventoryItem = remainingInventoryItems[0];

  return {
    ...inventory,
    areaId: primaryInventoryItem?.areaId ?? inventory?.areaId ?? null,
    zoneId: primaryInventoryItem?.zoneId ?? inventory?.zoneId ?? null,
    inventoryItems: {
      ...inventory?.inventoryItems,
      items: remainingInventoryItems,
    },
    shipmentLineItems: {
      ...inventory?.shipmentLineItems,
      items: remainingShipmentLineItems,
    },
  };
};

export const trimLeasedZoneInventories = (
  inventories,
  accountId,
  leasedZoneMap = getLeasedZoneMap(accountId),
) =>
  (inventories || [])
    .map((inventory) =>
      trimLeasedZoneInventory(inventory, accountId, leasedZoneMap),
    )
    .filter(Boolean);

export const getLeasedZones = (
  accountId,
  leasedZoneMap = getLeasedZoneMap(accountId),
) => {
  if (!accountId || !leasedZoneMap.size) return [];
  return Array.from(leasedZoneMap.values());
};

const normalizeStorageEntryKind = (entryKind, accrual) => {
  if (
    accrual === FeeAccrual.FLAT_ONHAND &&
    entryKind === StorageEntryKind.ONHAND_CURRENT
  ) {
    return StorageEntryKind.ONHAND_ASOF_ENDDATE;
  }
  return entryKind;
};

const chargeRulesCache = new Map();
const chargeRulesRefreshPromises = new Map();
// Bumped on every cache mutation. A refresh started before the bump is
// discarded on arrival - it holds a pre-mutation snapshot.
let chargeRulesGeneration = 0;
// Ids soft-deleted this session. rulesByAccountByActiveByEvent is a GSI, so
// reads are eventually consistent: a fetch right after the delete can still
// return the rule with its pre-delete active="true" projection (filtering the
// response by `active` would not help - the stale item carries the old
// value). Subtract these ids from active-only reads until the index catches
// up. activeOnly=false reads are exempt: the account purge sweeps inactive
// rules on purpose.
const softDeletedRuleIds = new Set();

const getChargeRulesCacheKey = (accountId, event, activeOnly) =>
  JSON.stringify({
    accountId,
    event: event || null,
    activeOnly,
  });

const refreshChargeRulesCache = async (accountId, event, activeOnly = true) => {
  const cacheKey = getChargeRulesCacheKey(accountId, event, activeOnly);
  const existingRefresh = chargeRulesRefreshPromises.get(cacheKey);

  if (existingRefresh) return existingRefresh;

  const generation = chargeRulesGeneration;
  const refreshPromise = fetchChargeRules(accountId, event, activeOnly)
    .then((rules) => {
      if (generation === chargeRulesGeneration)
        chargeRulesCache.set(cacheKey, rules);
      return rules;
    })
    .finally(() => {
      // Only this refresh's own registration. An invalidation that lands
      // mid-flight drops this entry (and the cached rules with it), and the
      // read that follows registers a newer refresh under the same key; a
      // blind delete here would evict that successor on arrival and let the
      // read after it start a duplicate fetch of what is already in flight.
      if (chargeRulesRefreshPromises.get(cacheKey) === refreshPromise)
        chargeRulesRefreshPromises.delete(cacheKey);
    });

  chargeRulesRefreshPromises.set(cacheKey, refreshPromise);
  return refreshPromise;
};

export const invalidateChargeRulesCache = (accountId) => {
  // No accountId = nothing to invalidate. Never escalate to a global clear:
  // hasOnPullStorageRule reads this cache synchronously and answers false
  // for every account while it is empty.
  if (!accountId) return;
  chargeRulesGeneration += 1;
  for (const cacheKey of [...chargeRulesCache.keys()]) {
    if (JSON.parse(cacheKey).accountId === accountId) {
      chargeRulesCache.delete(cacheKey);
      chargeRulesRefreshPromises.delete(cacheKey);
    }
  }
};

/**
 * Drop specific rules from every cached list for their account.
 *
 * Preferred over invalidateChargeRulesCache after a delete: the cache stays
 * populated and correct, so hasOnPullStorageRule (cache-only, never
 * fetches) keeps gating invoicing instead of silently answering false.
 */
export const removeChargeRulesFromCache = (rules = []) => {
  const idsByAccount = new Map();
  (rules || []).forEach((rule) => {
    if (!rule?.id || !rule?.accountId) return;
    if (!idsByAccount.has(rule.accountId))
      idsByAccount.set(rule.accountId, new Set());
    idsByAccount.get(rule.accountId).add(rule.id);
  });
  if (!idsByAccount.size) return;

  chargeRulesGeneration += 1;
  for (const [cacheKey, cached] of chargeRulesCache.entries()) {
    const removedIds = idsByAccount.get(JSON.parse(cacheKey).accountId);
    if (!removedIds) continue;
    chargeRulesCache.set(
      cacheKey,
      (cached || []).filter((rule) => !removedIds.has(rule.id)),
    );
  }
};

export const fetchSetupData = async (
  account,
  setAreas,
  setServices,
  setRailEquipmentChoices,
  setTruckEquipmentChoices,
) => {
  const areaPromise = API.graphql(
    graphqlOperation(areaByActiveBySortKey, {
      reloadYardId: window.appUser?.reloadYardId,
      activeSortKey: { beginsWith: { active: "true" } },
      limit: 1000,
    }),
  );

  const servicesPromise = fetchServices(account.id, true, true);
  const carrierEquipmentPromise = fetchCarrierEquipmentTypes();

  const [areaResp, servicesResp, carrierEquipmentResp] = await Promise.all([
    areaPromise,
    servicesPromise,
    carrierEquipmentPromise,
  ]);

  const areas = areaResp.data.areaByActiveBySortKey.items;
  const services = servicesResp;
  const carrierEquipment = carrierEquipmentResp;

  window.appUser.reloadYard.areas.items = areas;
  setupLocationMaps(window.appUser.reloadYard);
  setAreas(areas);
  setServices(services);
  setRailEquipmentChoices(carrierEquipment.railEquipmentTypes);
  setTruckEquipmentChoices(carrierEquipment.truckEquipmentTypes);
};

export const getChargeRules = async (accountId, event, activeOnly = true) => {
  if (!accountId) throw new Error("Account ID is required");

  const cacheKey = getChargeRulesCacheKey(accountId, event, activeOnly);
  const hasCachedRules = chargeRulesCache.has(cacheKey);

  if (hasCachedRules) {
    refreshChargeRulesCache(accountId, event, activeOnly).catch(() => {});

    return chargeRulesCache.get(cacheKey);
  }

  return refreshChargeRulesCache(accountId, event, activeOnly);
};

const PERIODIC_STORAGE_ACCOUNT_BATCH = 25;

/**
 * Keeps the accounts that bill storage on a time-based (periodic) frequency,
 * for pickers that only make sense for those accounts. ChargeRule has no
 * per-yard index, so rules are read per account in batches. An account whose
 * rules cannot be read is kept: the caller still validates the selection once
 * it loads that account's rules.
 *
 * Reads fresh rules, never the cached ones: no rule write updates the cache,
 * so a cached "no periodic rule" would hide an account that has since gained
 * one, and nothing re-runs this filter when a background refresh lands. The
 * refresh still fills the cache for its synchronous readers and shares any
 * read already in flight. The rules index is eventually consistent, so a
 * rule saved a moment before the picker mounts can still be missed once.
 */
export const filterPeriodicStorageAccounts = async (accounts = [], notify) => {
  const keep = new Set();
  const failures = [];

  for (const batch of chunkArray(accounts, PERIODIC_STORAGE_ACCOUNT_BATCH)) {
    await Promise.all(
      batch.map(async (account) => {
        try {
          const rules = await refreshChargeRulesCache(
            account.id,
            ChargeEvent.STORAGE,
          );
          if (getPeriodicStorageFrequencies(rules).length) keep.add(account.id);
        } catch (err) {
          failures.push(err);
          keep.add(account.id);
        }
      }),
    );
  }

  if (failures.length) {
    logError(
      "ChargeRuleUtils:filterPeriodicStorageAccounts",
      failures[0],
      notify,
    );
  }
  return accounts.filter((account) => keep.has(account.id));
};

/**
 * ON_PULL storage runs until a rail car is pulled (collectedDateTime), which
 * comes after the shipment is released, so invoicing a released car before
 * the pull would bill no storage at all. Callers block invoicing an
 * uncollected rail car while this answers true. ON_RELEASE needs no such gate:
 * a released shipment already has its end date.
 */
export const hasOnPullStorageRule = (accountId, activeOnly = true) => {
  if (!accountId) return false;

  for (const [cacheKey, rules] of chargeRulesCache.entries()) {
    const cachedKey = JSON.parse(cacheKey);

    if (cachedKey.accountId !== accountId) continue;
    if (cachedKey.activeOnly !== activeOnly) continue;
    if (cachedKey.event && cachedKey.event !== ChargeEvent.STORAGE) continue;

    if (
      rules?.some(
        (rule) =>
          rule?.event === ChargeEvent.STORAGE &&
          rule?.frequency === StorageFrequency.ON_PULL,
      )
    ) {
      return true;
    }
  }

  return false;
};

export const fetchChargeRules = async (accountId, event, activeOnly = true) => {
  if (!accountId) throw new Error("Account ID is required");
  let allItems = [];
  let nextToken = null;

  do {
    const input = {
      accountId: accountId,
      ...(activeOnly && {
        activeEvent: {
          beginsWith: {
            active: "true",
            ...(event && { event }),
          },
        },
      }),
      limit: MAX_ROWS_UPLOAD,
      nextToken,
    };

    const resp = await API.graphql(
      graphqlOperation(rulesByAccountByActiveByEvent, input),
    );

    const chargeRuleData = resp.data.rulesByAccountByActiveByEvent;
    allItems = [...allItems, ...chargeRuleData.items];
    nextToken = chargeRuleData.nextToken;
  } while (nextToken);

  const items = activeOnly
    ? allItems.filter((rule) => !softDeletedRuleIds.has(rule.id))
    : allItems;

  return sortChargeRules(
    items.map((rule) => ({
      ...rule,
      graduatedRates: normalizeGraduatedRates(rule?.graduatedRates),
    })),
  );
};

const SOFT_DELETE_BATCH_SIZE = 10;

/**
 * Soft delete charge rules by flipping active to "false".
 *
 * The rule drops out of rulesByAccountByActiveByEvent / rulesByService (both
 * query on the active#event sort key), so it disappears from the account rule
 * list and from manual charge pickers. Any WorkChargeRule still pointing at it
 * keeps resolving, so charges already added to open shipments still bill.
 *
 * `event` must be sent alongside `active`: both are members of the activeEvent
 * composite sort key and AppSync rejects a partial key update.
 */
export const softDeleteChargeRules = async (rules = []) => {
  const targets = [];
  const failed = [];

  // A rule without id/event cannot be updated (activeEvent is a composite
  // key). Report it as failed - never silently drop it, callers read
  // failed.length as "did anything go wrong".
  (rules || []).filter(Boolean).forEach((rule) => {
    if (rule.id && rule.event) targets.push(rule);
    else
      failed.push({
        rule,
        reason: new Error(
          `Charge rule ${rule.id || "(no id)"} is missing ${
            rule.id ? "event" : "id"
          }; cannot delete`,
        ),
      });
  });

  if (!targets.length) return { deleted: [], failed };

  // Chunked to avoid an unbounded mutation burst on large selections.
  const results = [];
  for (let i = 0; i < targets.length; i += SOFT_DELETE_BATCH_SIZE) {
    const batch = targets.slice(i, i + SOFT_DELETE_BATCH_SIZE);
    results.push(
      ...(await Promise.allSettled(
        batch.map((rule) =>
          API.graphql(
            graphqlOperation(updateChargeRule, {
              input: { id: rule.id, active: "false", event: rule.event },
            }),
          ),
        ),
      )),
    );
  }

  const deleted = [];
  results.forEach((result, index) => {
    if (result.status === "fulfilled") deleted.push(targets[index]);
    else failed.push({ rule: targets[index], reason: result.reason });
  });

  deleted.forEach(({ id }) => softDeletedRuleIds.add(id));
  removeChargeRulesFromCache(deleted);

  return { deleted, failed };
};

export const fetchInboundBillingRules = async (accountId) => {
  if (!accountId) throw new Error("Account ID is required");

  const [inboundRules, storageRules] = await Promise.all([
    fetchChargeRules(accountId, ChargeEvent.INBOUND, true),
    fetchChargeRules(accountId, ChargeEvent.STORAGE, true),
  ]);

  return sortChargeRules([
    ...(inboundRules || []),
    ...(storageRules || []).filter(
      (rule) =>
        rule?.frequency === StorageFrequency.ON_RELEASE ||
        rule?.frequency === StorageFrequency.ON_PULL,
    ),
  ]);
};

export const fetchOutboundBillingRules = async (accountId) => {
  if (!accountId) throw new Error("Account ID is required");

  const [outboundRules, storageRules] = await Promise.all([
    fetchChargeRules(accountId, ChargeEvent.OUTBOUND, true),
    fetchChargeRules(accountId, ChargeEvent.STORAGE, true),
  ]);

  return sortChargeRules([
    ...(outboundRules || []),
    ...(storageRules || []).filter(
      (rule) =>
        rule?.frequency === StorageFrequency.ON_RELEASE ||
        rule?.frequency === StorageFrequency.ON_PULL ||
        rule?.frequency === StorageFrequency.ON_DEPLETION,
    ),
  ]);
};

export const normalizeGraduatedRates = (rates) => {
  if (!Array.isArray(rates)) return [];

  return rates
    .map((tier, index) => ({
      days: tier?.days === "" || tier?.days == null ? null : Number(tier?.days),
      // The first tier always derives its rate from the current service price.
      rate:
        index === 0 || tier?.rate === "" || tier?.rate == null
          ? null
          : Number(tier?.rate),
    }))
    .filter((tier, index, tiers) => {
      const hasValidDays =
        tier.days == null
          ? tiers.length > 1 && index === tiers.length - 1
          : Number.isFinite(tier.days) && tier.days > 0;
      const hasValidRate =
        index === 0 || (Number.isFinite(tier.rate) && tier.rate >= 0);

      return hasValidDays && hasValidRate;
    });
};

/**
 * Free railcars only mean something on a periodic per-railcar storage rule, so
 * every other rule saves null and a stale value cannot outlive a carrier-type
 * or frequency change. Whole cars only; nothing or zero is no allowance.
 *
 * The frequency test is not cosmetic: only the periodic storage report
 * allocates free-car slots, so an allowance saved on an ON_RELEASE, ON_PULL
 * or ON_DEPLETION rule is shown in the Free Railcars column and then honoured
 * by nothing. The form refuses that combination; so does this, for the CSV
 * import and every other caller.
 */
export const normalizeFreeCars = (rule) => {
  if (rule?.event !== ChargeEvent.STORAGE) return null;
  if (rule?.carrierType !== CarrierType.RAIL) return null;
  if (isEventDrivenStorageFrequency(rule?.frequency)) return null;
  if (rule.freeCars === "" || rule.freeCars == null) return null;
  const cars = Math.floor(Number(rule.freeCars));
  return Number.isFinite(cars) && cars > 0 ? cars : null;
};

export const composeNewChargeRule = (item, services, account) => {
  const service = services.find((service) => service.name === item.serviceName);

  const input = {
    active: "true",
    groups: account.groups,
    accountId: account.id,
    reloadYardId: account.reloadYardId,

    event: item.event,
    serviceId: service?.id || item.serviceId,
    billingUOM: item.billingUOM,

    reloadYardCommodityId: item.reloadYardCommodityId,
    carrierType: item.carrierType,
    carrierEquipmentTypeId: item.carrierEquipmentTypeId || null,

    areaId: item.areaId,
    zoneId: item.zoneId,

    frequency: item.frequency,
    feeAccrual: item.feeAccrual,
    freeDaysPolicy: item.freeDaysPolicy || FreeDaysPolicy.DAYS,
    freeDays: item.freeDays ?? null,
    freeCars: normalizeFreeCars(item),
    graduatedRates: normalizeGraduatedRates(item.graduatedRates),

    minCharge: item.minCharge || null,
    maxCharge: item.maxCharge || null,

    execution: item.execution,
  };
  // History will be logged via logHistory after successful creation
  return input;
};

// #########################################################
//                   RULE SORT AND VALIDATION
// #########################################################
/**
 * Sorts charge rules by:
 * 1. event
 * 2. specificity (more scoped first): carrierType, carrierEquipmentTypeId, reloadYardCommodityId, areaId, zoneId
 * 3. serviceId (stable tie-breaker across services)
 * 4. billingUOM
 * 5. createdAt (descending)
 */
export const sortChargeRules = (rules) => {
  const norm = (v) => (v === "" || v == null ? null : v);

  const presenceCount = (rule, fields) =>
    fields.reduce((n, f) => n + (norm(rule[f]) != null ? 1 : 0), 0);

  const cmpPresenceThenValue = (a, b, field) => {
    const va = norm(a[field]);
    const vb = norm(b[field]);
    if (va === vb) return 0;
    if (va == null) return 1; // nulls last (less specific)
    if (vb == null) return -1; // non-nulls first (more specific)
    return String(va).localeCompare(String(vb));
  };

  const WHEN = [
    "carrierType",
    "carrierEquipmentTypeId",
    "reloadYardCommodityId",
  ];
  const WHERE = ["areaId", "zoneId"];
  const SCOPE_ORDER = [...WHERE, ...WHEN];

  return [...rules].sort((a, b) => {
    // Level 1: event
    if (a.event !== b.event)
      return String(a.event).localeCompare(String(b.event));

    // Level 2: specificity across scope fields (more constraints first)
    const aScope = presenceCount(a, SCOPE_ORDER);
    const bScope = presenceCount(b, SCOPE_ORDER);
    if (aScope !== bScope) return bScope - aScope;

    // Then compare by ordered scope field values, with nulls last
    for (const field of SCOPE_ORDER) {
      const c = cmpPresenceThenValue(a, b, field);
      if (c) return c;
    }

    // Level 3: serviceId as a stable tie-breaker across services
    if (a.serviceId !== b.serviceId)
      return String(a.serviceId).localeCompare(String(b.serviceId));

    // Then billingUOM
    if (a.billingUOM !== b.billingUOM) {
      return String(a.billingUOM).localeCompare(String(b.billingUOM));
    }

    // Tie-breaker: createdAt desc
    const aCreated = new Date(a.createdAt || 0);
    const bCreated = new Date(b.createdAt || 0);
    return bCreated - aCreated;
  });
};

const norm = (v) => (v === "" || v == null ? null : v);

// Count how many scope fields are concretely specified on a rule
const specificityCount = (r) =>
  RULE_SCOPE_FIELDS.reduce((n, f) => n + (norm(r[f]) != null ? 1 : 0), 0);

const overlapsOnAllScopes = (a, b) => {
  for (const f of RULE_SCOPE_FIELDS) {
    const va = norm(a[f]),
      vb = norm(b[f]);
    if (va != null && vb != null && va !== vb) return false; // hard mismatch
  }
  return true; // either equal or at least one side is wildcard on each field
};

const isEqualScope = (a, b) =>
  RULE_SCOPE_FIELDS.every((f) => norm(a[f]) === norm(b[f]));

const isStorageRule = (rule) => rule?.event === ChargeEvent.STORAGE;

const isPeriodicStorageRule = (rule) =>
  isStorageRule(rule) && isPeriodicStorageFrequency(rule?.frequency);

const isEventDrivenStorageRule = (rule) =>
  isStorageRule(rule) && isEventDrivenStorageFrequency(rule?.frequency);

const isOnReleaseAndOnDepletionPair = (a, b) =>
  [a?.frequency, b?.frequency].includes(StorageFrequency.ON_RELEASE) &&
  [a?.frequency, b?.frequency].includes(StorageFrequency.ON_DEPLETION);

// ON_PULL bills the railcar's stay on its own shipment, usually the inbound.
// ON_DEPLETION bills the inventory's stay on the outbound that empties it,
// often the last of several. Separate charges built by separate passes, so the
// two never compete for one record and their scopes need not nest.
const isOnPullAndOnDepletionPair = (a, b) =>
  isStorageRule(a) &&
  isStorageRule(b) &&
  [a?.frequency, b?.frequency].includes(StorageFrequency.ON_PULL) &&
  [a?.frequency, b?.frequency].includes(StorageFrequency.ON_DEPLETION);

const getComparableChargeRulePeers = (rule, existingRules = []) => {
  const numberedRules = existingRules.map((existingRule, index) => ({
    ...existingRule,
    ruleNumber: index + 1,
  }));

  const scopeExecution =
    rule.execution === BillingAutomation.AUTO ||
    rule.execution === BillingAutomation.MANUAL;

  return numberedRules.filter((existingRule) => {
    if (existingRule.id === rule.id) return false;
    if (existingRule.event !== rule.event) return false;
    if (scopeExecution && existingRule.execution !== rule.execution)
      return false;
    return true;
  });
};

const classifyStorageBillingPair = (a, b) => {
  if (!isStorageRule(a) || !isStorageRule(b)) return "none";
  if (a.id === b.id) return "none";
  if (a.active !== "true" || b.active !== "true") return "none";
  if (a.serviceId !== b.serviceId) return "none";
  if (!overlapsOnAllScopes(a, b)) return "none";

  const mixedPeriodicAndEventDriven =
    (isPeriodicStorageRule(a) && isEventDrivenStorageRule(b)) ||
    (isEventDrivenStorageRule(a) && isPeriodicStorageRule(b));
  if (mixedPeriodicAndEventDriven) return "storage_billing_mode_conflict";

  if (
    isEventDrivenStorageRule(a) &&
    isEventDrivenStorageRule(b) &&
    isOnReleaseAndOnDepletionPair(a, b)
  ) {
    return "storage_double_event_conflict";
  }

  return "none";
};

export const validateStorageRuleConflicts = (rule, existingRules = []) => {
  if (!isStorageRule(rule) || !rule?.serviceId) return {};

  const peers = existingRules
    .map((existingRule, index) => ({ ...existingRule, ruleNumber: index + 1 }))
    .filter((existingRule) => {
      if (existingRule.id === rule.id) return false;
      if (existingRule.event !== rule.event) return false;
      return true;
    });

  for (let i = 0; i < peers.length; i++) {
    const other = peers[i];
    const kind = classifyStorageBillingPair(rule, other);

    if (kind === "storage_billing_mode_conflict") {
      return {
        _error: `Conflicts with rule # ${other.ruleNumber}. Same service/scope cannot mix periodic and event-driven storage billing.`,
        index: i,
        kind,
      };
    }

    if (kind === "storage_double_event_conflict") {
      return {
        _error: `Conflicts with rule # ${other.ruleNumber}. Same service/scope cannot combine On Release and On Depletion storage billing.`,
        index: i,
        kind,
      };
    }
  }

  return {};
};

/** A is strictly more specific than B (A ⊂ B): every concrete constraint in B is matched by A,
 * and A adds at least one extra concrete constraint where B is null. */
const isMoreSpecificThan = (a, b) => {
  let strictlyMore = false;
  for (const f of RULE_SCOPE_FIELDS) {
    const va = norm(a[f]),
      vb = norm(b[f]);
    if (vb != null && va !== vb) return false; // A must match B’s concrete values
    if (vb == null && va != null) strictlyMore = true; // A adds constraint
  }
  return strictlyMore;
};

const numOrNull = (v) => (v === "" || v == null ? null : Number(v));

/**
 * True when two rules bill identically once selected: same rule-level
 * pricing constraints and, for STORAGE, same storage parameters. The rate
 * itself lives on the Service (per UOM), so these are the only fields a
 * more-specific child rule can legitimately change.
 */
const sameRuleParams = (a, b) => {
  if (numOrNull(a.minCharge) !== numOrNull(b.minCharge)) return false;
  if (numOrNull(a.maxCharge) !== numOrNull(b.maxCharge)) return false;
  if (
    JSON.stringify(normalizeGraduatedRates(a.graduatedRates)) !==
    JSON.stringify(normalizeGraduatedRates(b.graduatedRates))
  ) {
    return false;
  }
  if (a.event !== ChargeEvent.STORAGE) return true;

  if (norm(a.frequency) !== norm(b.frequency)) return false;
  if (norm(a.feeAccrual) !== norm(b.feeAccrual)) return false;
  if ((numOrNull(a.freeCars) ?? 0) !== (numOrNull(b.freeCars) ?? 0)) {
    return false;
  }
  const policyA = a.freeDaysPolicy || FreeDaysPolicy.DAYS;
  const policyB = b.freeDaysPolicy || FreeDaysPolicy.DAYS;
  if (policyA !== policyB) return false;
  // Under END_OF_MONTH the numeric freeDays is not consulted.
  if (policyA === FreeDaysPolicy.END_OF_MONTH) return true;
  return (numOrNull(a.freeDays) ?? 0) === (numOrNull(b.freeDays) ?? 0);
};

/**
 * Classify relationship for rules within the same event.
 * Returns one of: 'conflict' | 'duplicate' | 'redundant' | 'override' | 'ambiguous' | 'none'
 *
 * Invariant enforced: two rules that can both match a record must differ in
 * specificity — nested (one strictly contains the other) or carrying a
 * different count of scope constraints. Runtime selection is then decided by
 * specificity count alone and never falls through to a field-order tie-break.
 *
 * - duplicate:  same scope, same service, same UOM
 * - conflict:   same scope, same service, different UOM; or same scope across
 *               STORAGE services (only one storage rule can apply)
 * - redundant:  parent/child, same service, same UOM, identical rule params
 *               (child changes nothing; broader is shadowed)
 * - override:   parent/child otherwise, or cross-specific with differing
 *               constraint counts (deterministic, more-specific wins)
 * - ambiguous:  cross-specific overlap (neither ⊂ other) with the same
 *               number of scope constraints, within the same service or
 *               across STORAGE services. Which rule wins depends on internal
 *               sort order, so the user must refine one rule until the counts
 *               differ or one sits fully inside the other.
 * - none:       no overlap, inactive, different event, or cross-service
 *               overlap for non-STORAGE events (multiple services may bill one shipment)
 */
export const classifyRulePair = (a, b) => {
  if (a.id === b.id) return "none";
  if (a.active !== "true" || b.active !== "true") return "none";
  if (a.event !== b.event) return "none";
  if (isOnPullAndOnDepletionPair(a, b)) return "none";

  /**
   * Gate 1 — Can these two rules ever both apply?
   * If their scopes don't "overlap", there is no world in which the same shipment
   * would satisfy both rules, so they don't interact at all.
   * Overlap definition:
   * - For each scoping field (carrierType, carrierEquipmentTypeId, reloadYardCommodityId, areaId, zoneId),
   *   either the values are equal OR at least one side is null/empty (treated as a wildcard).
   * - If any field is a hard mismatch (both concrete and different), the rules cannot co-apply.
   * Result: when scopes don't overlap, classification is "none".
   */
  if (!overlapsOnAllScopes(a, b)) return "none";

  // Shipment-level ambiguity guard for STORAGE rules:
  // If exactly one rule is shipment-level (has carrierType) and both have equal specificity
  // (same count of scoped fields present), treat as conflict regardless of service/UOM.
  if (
    a.event === "STORAGE" &&
    !!norm(a.carrierType) !== !!norm(b.carrierType) &&
    specificityCount(a) === specificityCount(b)
  ) {
    return "conflict";
  }

  /**
   * Gate 2 — Special case: identical scope (same target audience).
   * If all scoping fields are exactly the same (including both null), then both rules
   * are competing for the very same shipments:
   * - Same billingUOM  → they are functionally the same rule ⇒ "duplicate"
   * - Different UOM    → they create an ambiguity for the same shipments ⇒ "conflict"
   * Note:
   * - Nulls in scope mean “no constraint”; equal-scope with nulls still means both rules
   *   target the same audience (the unfiltered set).
   *
   * Examples (same event+service implied):
   * 1) A:{carrier=RAIL, area=Yard}  vs  B:{carrier=RAIL, area=Yard}
   *    - billingUOM equal   → duplicate
   *    - billingUOM differ  → conflict
   * 2) A:{carrier=RAIL}  vs  B:{carrier=TRUCK}
   *    - no scope overlap   → none
   * 3) A:{carrier=RAIL}  vs  B:{carrier=null}
   *    - overlap, not equal scope; classification decided later (override/redundant/conflict)
   */
  const sameService = a.serviceId === b.serviceId;
  const sameUOM = a.billingUOM === b.billingUOM;
  if (isEqualScope(a, b)) {
    // Identical scope:
    // - Same service → duplicate or conflict based on UOM
    // - Different services:
    //     * STORAGE: treat as conflict to prevent nondeterministic selection (only one rule can apply)
    //     * Others: allowed; per-service selection handles ambiguity
    if (!sameService) {
      if (a.event === "STORAGE") return "conflict";
      return "none";
    }
    return sameUOM ? "duplicate" : "conflict";
  }

  const aMore = isMoreSpecificThan(a, b);
  const bMore = isMoreSpecificThan(b, a);

  if (aMore || bMore) {
    // Parent/child: top-down selection is deterministic (specific wins).
    // Same service + same UOM is redundant only when the child bills exactly
    // like the parent; a child that changes free days, accrual, tiers or
    // min/max is a legitimate specialization.
    if (sameService && sameUOM && sameRuleParams(a, b)) return "redundant";
    return "override";
  }

  // Cross-specific overlap (orthogonal constraints) — both can match a record,
  // neither contains the other. Runtime selection is still deterministic when
  // the constraint counts differ: sortChargeRules ranks by specificity count
  // before any field-order comparison, so the rule with more constraints
  // always wins (e.g. a per-car carrierType rule under a commodity+zone rule).
  // Only an equal count falls through to field-order tie-breaks.
  const equalSpecificity = specificityCount(a) === specificityCount(b);
  if (sameService) return equalSpecificity ? "ambiguous" : "override";
  // Across STORAGE services only one rule is applied per item, so the same
  // reasoning applies. Other events bill each service independently.
  if (a.event === ChargeEvent.STORAGE)
    return equalSpecificity ? "ambiguous" : "override";
  return "none";
};

/**
 * Validate one rule against an array (excluding self).
 * Returns the first *blocking* issue (conflict). Optionally you can surface duplicates/redundant/override as warnings.
 */
export const validateChargeRule = (rule, existingRules = []) => {
  // guard: don’t run conflict checks until required “what” fields exist
  if (!rule?.event || !rule?.serviceId || !rule?.billingUOM) return {};

  // Only rail records a pull (collectedDateTime); anything else would never
  // reach the end of its stay and never bill.
  if (
    isStorageRule(rule) &&
    rule.frequency === StorageFrequency.ON_PULL &&
    rule.carrierType !== CarrierType.RAIL
  ) {
    return {
      _error: ON_PULL_REQUIRES_RAIL,
      kind: "on_pull_requires_rail",
    };
  }

  const storageValidation = validateStorageRuleConflicts(rule, existingRules);
  if (storageValidation._error) return storageValidation;

  const peers = getComparableChargeRulePeers(rule, existingRules);

  for (let i = 0; i < peers.length; i++) {
    const other = peers[i];
    const kind = classifyRulePair(rule, other);

    if (kind === "ambiguous") {
      return {
        _error: `Ambiguous with rule # ${other.ruleNumber}: scopes overlap but neither contains the other. Add the missing filter (e.g. Area or Load Type) to one rule so it sits fully inside the other.`,
        index: i,
        kind,
      };
    }

    if (["conflict", "duplicate", "redundant"].includes(kind)) {
      const label =
        kind === "conflict"
          ? "Conflicts with"
          : kind === "duplicate"
            ? "Duplicate of"
            : "Redundant with";

      return {
        _error: `${label} rule # ${other.ruleNumber}.`,
        index: i,
        kind,
      };
    }
    // override still allowed
  }
  return {};
};

/**
 * Build a string key for a rule's scope (event + serviceId + all scope discriminators).
 * Used to detect when an imported rule will override an existing rule (same key).
 */
const scopeKeyNorm = (v) => (v === "" || v == null ? "null" : String(v).trim());

export const getRuleScopeKey = (rule, serviceId) => {
  if (!rule || !serviceId) return "";
  const parts = [
    rule.event,
    serviceId,
    ...RULE_SCOPE_FIELDS.map((f) => scopeKeyNorm(rule[f])),
  ];
  return parts.join("_");
};

/**
 * Find STORAGE rules in the same (serviceId, areaId, zoneId) scope as the given rule.
 * Excludes the rule itself (by id) and non-STORAGE events.
 * Used only to keep frequency / fee accrual consistent within a location.
 * Free days are per rule and may differ across commodities in one location.
 */
export const getStoragePeersInSameScope = (rule, allRules = []) => {
  if (!rule || !Array.isArray(allRules)) return [];
  if (rule.event !== "STORAGE") return [];

  const scopeService = norm(rule.serviceId);
  const scopeArea = norm(rule.areaId);
  const scopeZone = norm(rule.zoneId);

  return allRules.filter((r) => {
    if (!r) return false;
    if (r.id && rule.id && r.id === rule.id) return false;
    if (r.event !== "STORAGE") return false;

    return (
      norm(r.serviceId) === scopeService &&
      norm(r.areaId) === scopeArea &&
      norm(r.zoneId) === scopeZone
    );
  });
};

// #########################################################
//                GENERATE INVOICE LINE ITEMS
// #########################################################
/**
 * Check if a charge rule applies to the current record (shipment, inventory, etc.)
 * @param {Object} rule - Charge rule object
 * @param {Object} record - Record data matching the event type
 * @returns {boolean} True if rule applies
 */
export const doesRuleApply = (rule, record) => {
  // WHEN filters
  // Check shipment.carrierType for shipment billing
  // Check inventory.inboundShipment?.carrierType for inventory storage billing
  const recordCarrierType =
    record.carrierType || record.inboundShipment?.carrierType;
  const hasRuleCarrierType = !!rule.carrierType;
  // Carrier type match (or catch-all when rule has no carrier type)
  const carrierTypeMatches =
    !hasRuleCarrierType || rule.carrierType === recordCarrierType;
  if (!carrierTypeMatches) return false;

  // Match on carrier equipment type when provided
  const recordCarrierEquipmentTypeId =
    record.carrierEquipmentTypeId ||
    record.inboundShipment?.carrierEquipmentTypeId;
  if (rule.carrierEquipmentTypeId) {
    // Equipment type requires explicit carrier type on the rule
    if (!hasRuleCarrierType) return false;
    // Equipment type match
    if (rule.carrierEquipmentTypeId !== recordCarrierEquipmentTypeId) {
      return false;
    }
  }

  // Match on load type
  if (
    rule.reloadYardCommodityId &&
    rule.reloadYardCommodityId !== record.reloadYardCommodityId
  ) {
    return false;
  }

  // WHERE filters
  const recordAreaId = record.areaId || record.inboundShipment?.areaId;
  const recordZoneId = record.zoneId || record.inboundShipment?.zoneId;
  if (rule.areaId && rule.areaId !== recordAreaId) return false;
  if (rule.zoneId && rule.zoneId !== recordZoneId) return false;

  // Rule does apply
  return true;
};

/**
 * Filter applicable MANUAL rules for a given record, with optional event constraint.
 * - Respects wildcard semantics: blank zone / equipment / commodity / area match all.
 * - Uses the same matching logic as automatic billing (see doesRuleApply).
 *
 * @param {Object} shipment - Shipment / inventory-like record (must carry carrierType, areaId, zoneId, reloadYardCommodityId, etc).
 * @param {Array} rules - Charge rules (typically from fetchChargeRules for the account).
 * @param {string} event - Optional ChargeEvent filter (e.g., ChargeEvent.INBOUND).
 * @returns {Array} Subset of rules that are MANUAL and applicable to the record.
 */
export const filterApplicableManualRules = (shipment, rules = [], event) => {
  if (!shipment || !Array.isArray(rules) || !rules.length) return [];

  return rules.filter((rule) => {
    if (!rule) return false;
    if (rule.active && rule.active !== "true") return false;
    if (rule.execution !== BillingAutomation.MANUAL) return false;
    if (event) {
      const isInboundManualRule =
        event === ChargeEvent.INBOUND && rule.event === ChargeEvent.INBOUND;
      const isInboundShipmentStorageRule =
        event === ChargeEvent.INBOUND &&
        rule.event === ChargeEvent.STORAGE &&
        (rule.frequency === StorageFrequency.ON_RELEASE ||
          rule.frequency === StorageFrequency.ON_PULL);
      const isOutboundManualRule =
        event === ChargeEvent.OUTBOUND && rule.event === ChargeEvent.OUTBOUND;
      const isOutboundStorageRule =
        event === ChargeEvent.OUTBOUND &&
        rule.event === ChargeEvent.STORAGE &&
        (rule.frequency === StorageFrequency.ON_RELEASE ||
          rule.frequency === StorageFrequency.ON_PULL ||
          rule.frequency === StorageFrequency.ON_DEPLETION);

      if (
        !isInboundManualRule &&
        !isInboundShipmentStorageRule &&
        !isOutboundManualRule &&
        !isOutboundStorageRule &&
        rule.event !== event
      )
        return false;
    }
    return doesRuleApply(rule, shipment);
  });
};

/**
 * Build a user-friendly label for a MANUAL charge rule:
 * "Service Name : $rate / UOM name"
 * Falls back gracefully if rate or UOM are missing.
 */
export const getManualRuleLabel = (rule) => {
  if (!rule) return "";

  const serviceName =
    rule.service?.name || rule.serviceName || "(Unnamed Service)";
  const uom = rule.billingUOM || "";
  const uomName = uom ? getUnitName(uom) : "";

  let rate = null;
  if (rule.service && uom) {
    rate = getUnitPrice(rule.service, uom, null);
  }

  const hasRate = rate != null;
  const hasUom = !!(uomName || uom);
  const uomDisplay = uomName || uom;

  if (hasRate && hasUom)
    return `${serviceName} : $${rate.toFixed(2)} / ${uomDisplay}`;
  if (hasRate) return `${serviceName} : $${rate.toFixed(2)}`;
  if (hasUom) return `${serviceName} : ${uomDisplay}`;

  return serviceName;
};

/**
 * Short identifying label for a charge rule (history log, notifications).
 */
export const getChargeRuleLabel = (rule) => {
  if (!rule) return "";
  return [rule.event, getManualRuleLabel(rule)].filter(Boolean).join(" / ");
};

/**
 * Build SelectInput choices for a manual charge row.
 *
 * If the row already references a rule that is no longer in applicableRules
 * (rule was soft-deleted, or the shipment's carrier/area/zone changed so the
 * rule no longer applies), append it as a disabled "(unavailable)" choice so
 * the row still shows what it was billed as instead of rendering blank.
 * The label is built from the record's nested chargeRule when present.
 */
export const getManualRuleChoices = (applicableRules = [], scopedFormData) => {
  const choices = applicableRules.map((rule) => ({
    id: rule.id,
    name: getManualRuleLabel(rule),
  }));

  const selectedId = scopedFormData?.chargeRuleId;
  if (selectedId && !applicableRules.some((rule) => rule.id === selectedId)) {
    const rule = scopedFormData?.chargeRule;
    choices.push({
      id: selectedId,
      name: rule
        ? `${getManualRuleLabel(rule)} (unavailable)`
        : "(unavailable rule)",
      disabled: true,
    });
  }

  return choices;
};

/**
 * Find the most specific applicable charge rule for a record.
 * Works for inventory, inbound, outbound, etc.
 *
 * @param {Object} record - The record to evaluate (inventory, shipment, etc.)
 * @param {Array} rules - Pre-sorted array of charge rules
 * @returns {Object|null} Most specific applicable rule, or null if none apply
 */
export const findApplicableRule = (record, rules) => {
  if (!Array.isArray(rules) || rules.length === 0) return null;

  for (const rule of rules) {
    const isApplicable = doesRuleApply(rule, record);
    if (isApplicable) return rule;
  }

  return null;
};

/**
 * Find the most specific applicable rule per service.
 * Expects rules to be pre-sorted by specificity (see sortChargeRules).
 * Returns an array of chosen rules, one per serviceId, in the order encountered.
 */
export const findApplicableRulesPerService = (record, rules) => {
  if (!Array.isArray(rules) || rules.length === 0) return [];

  const chosenByService = new Map();

  for (const rule of rules) {
    if (chosenByService.has(rule.serviceId)) continue;
    if (doesRuleApply(rule, record)) {
      chosenByService.set(rule.serviceId, rule);
    }
  }

  return Array.from(chosenByService.values());
};

/**
 * Apply min/max charge constraints
 * @param {number} amount - Original amount
 * @param {number} minCharge - Minimum charge
 * @param {number} maxCharge - Maximum charge
 * @returns {number} Constrained amount
 */
export const applyChargeConstraints = (amount, minCharge, maxCharge) => {
  let finalAmount = amount;

  if (
    minCharge !== null &&
    minCharge !== undefined &&
    finalAmount < minCharge
  ) {
    finalAmount = minCharge;
  }

  if (
    maxCharge !== null &&
    maxCharge !== undefined &&
    finalAmount > maxCharge
  ) {
    finalAmount = maxCharge;
  }

  return finalAmount;
};

// #########################################################
//              INVENTORY STORAGE INVOICE HELPERS
// #########################################################

const isYMD = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

// Interpret yyyy-MM-dd as LOCAL midnight to match your period boundary semantics.
// Everything else (ISO datetime, Date) uses native Date parsing.
const toDateSafe = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;

  if (isYMD(v)) {
    const d = new Date(`${v}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Convenience for period boundaries (start/endDate strings from calculatePeriodDates)
const toBoundaryDate = (ymd) => toDateSafe(ymd);

const getShipmentDate = (lineItem) =>
  lineItem?.outboundShipment?.departureDateTime ?? lineItem?.updatedAt;

const isOnOrAfterBoundary = (dateValue, boundaryDate) => {
  const parsedDate = toDateSafe(dateValue);
  return !!(parsedDate && boundaryDate && parsedDate >= boundaryDate);
};

const wasRecordStoredAsOfBoundary = (status, depletedAt, endDate) => {
  const end = toBoundaryDate(endDate);

  return (
    status === InventoryStatus.STORED ||
    (status === InventoryStatus.SHIPPED && isOnOrAfterBoundary(depletedAt, end))
  );
};

const getInventoryDepletedAt = (inventory) => {
  const depletedAtCandidates = [
    inventory?.depletedAt,
    ...(inventory?.inventoryItems?.items || []).map((item) => item?.depletedAt),
  ];

  let latestDepletedAt = null;
  depletedAtCandidates.forEach((value) => {
    const parsedDate = toDateSafe(value);
    if (!parsedDate) return;
    if (!latestDepletedAt || parsedDate > latestDepletedAt) {
      latestDepletedAt = parsedDate;
    }
  });

  return latestDepletedAt;
};

const wasInventoryStoredAsOfEndDate = (inventory, endDate) =>
  wasRecordStoredAsOfBoundary(
    inventory?.status,
    getInventoryDepletedAt(inventory),
    endDate,
  );

const wasInventoryItemStoredAsOfEndDate = (inventoryItem, endDate) =>
  wasRecordStoredAsOfBoundary(
    inventoryItem?.status,
    inventoryItem?.depletedAt,
    endDate,
  );

/**
 * Build per-inventoryItem "on hand as of endDate" splits.
 *
 * Idea:
 *  onHandAsOfEndDate = currentOnHandNow + qtyShippedAfterEndDate
 *
 * - currentOnHandNow comes from inventory.inventoryItems with quantity still on hand
 * - qtyShippedAfterEndDate comes from shipmentLineItems SHIPPED on/after endDate
 *   using ShipmentLineItem -> InventoryItem join records when available.
 *
 * Falls back gracefully:
 * - if join records are missing, aggregates to an inventory-level (inventoryItemId: null) bucket.
 * - if inventoryItems are missing, locations fall back to inventory.areaId/zoneId.
 *
 * Returns: [{ inventoryItemId, areaId, zoneId, quantityOnHand, shippedAfterEndDate }]
 * where quantityOnHand is the computed AS-OF quantity.
 */
const getStorageSplitsOnHandAsOfEndDate = (inventory, endDate) => {
  const invAreaId = inventory?.areaId ?? null;
  const invZoneId = inventory?.zoneId ?? null;

  const inventoryItems = inventory?.inventoryItems?.items || [];
  const shipmentLineItems = inventory?.shipmentLineItems?.items || [];

  const end = toBoundaryDate(endDate);

  // Base map keyed by inventoryItemId
  const byInvItemId = new Map();

  // Seed from quantities that were still on hand as of the boundary.
  inventoryItems.forEach((ii) => {
    const id = ii?.id ?? null;
    if (!id) return;

    const currentOnHand = wasInventoryItemStoredAsOfEndDate(ii, endDate)
      ? Math.max(0, Number(ii?.quantityOnHand) || 0)
      : 0;

    byInvItemId.set(id, {
      inventoryItemId: id,
      areaId: ii?.areaId ?? invAreaId,
      zoneId: ii?.zoneId ?? invZoneId,
      currentOnHand,
      shippedAfterEndDate: 0,
    });
  });

  // Track fallback shipped-after qty when we can't attribute to inventoryItemId
  let unallocatedShippedAfter = 0;

  // Add shipped-after-endDate quantities
  shipmentLineItems
    .filter((sli) => sli?.status === ShipmentLineItemStatus.SHIPPED)
    .filter((sli) => isOnOrAfterBoundary(getShipmentDate(sli), end))
    .forEach((sli) => {
      const joins = sli?.inventoryItems?.items || [];
      if (joins.length) {
        joins.forEach((j) => {
          const invItemId = j?.inventoryItemId ?? null;
          const qty = Number(j?.quantityToPick) || 0;
          if (!invItemId || qty <= 0) return;

          const existing = byInvItemId.get(invItemId) || {
            inventoryItemId: invItemId,
            areaId: invAreaId,
            zoneId: invZoneId,
            currentOnHand: 0,
            shippedAfterEndDate: 0,
          };

          existing.shippedAfterEndDate =
            (Number(existing.shippedAfterEndDate) || 0) + qty;

          byInvItemId.set(invItemId, existing);
        });
      } else {
        // No join detail; fall back to inventory-level totalQuantityToPick
        const qty = Number(sli?.totalQuantityToPick) || 0;
        if (qty > 0) unallocatedShippedAfter += qty;
      }
    });

  const splits = Array.from(byInvItemId.values())
    .map((r) => {
      const qtyAsOf =
        (Number(r.currentOnHand) || 0) + (Number(r.shippedAfterEndDate) || 0);
      return {
        inventoryItemId: r.inventoryItemId,
        areaId: r.areaId ?? invAreaId,
        zoneId: r.zoneId ?? invZoneId,
        quantityOnHand: qtyAsOf,
        shippedAfterEndDate: Number(r.shippedAfterEndDate) || 0,
      };
    })
    .filter((s) => (Number(s.quantityOnHand) || 0) > 0);

  if (!inventoryItems.length) {
    const inventoryLevelOnHand = wasInventoryStoredAsOfEndDate(
      inventory,
      endDate,
    )
      ? Math.max(0, Number(inventory?.quantityOnHand) || 0)
      : 0;
    if (inventoryLevelOnHand > 0) {
      splits.push({
        inventoryItemId: null,
        areaId: invAreaId,
        zoneId: invZoneId,
        quantityOnHand: inventoryLevelOnHand,
        shippedAfterEndDate: 0,
      });
    }
  }

  // Inventory-level fallback only for shipped-after quantities we cannot attribute.
  if (unallocatedShippedAfter > 0) {
    const inventoryLevelSplit = splits.find(
      (split) => split.inventoryItemId == null,
    );
    const fallbackQuantity = unallocatedShippedAfter;

    if (inventoryLevelSplit) {
      inventoryLevelSplit.quantityOnHand =
        (Number(inventoryLevelSplit.quantityOnHand) || 0) + fallbackQuantity;
      inventoryLevelSplit.shippedAfterEndDate =
        (Number(inventoryLevelSplit.shippedAfterEndDate) || 0) +
        fallbackQuantity;
    } else {
      splits.push({
        inventoryItemId: null,
        areaId: invAreaId,
        zoneId: invZoneId,
        quantityOnHand: fallbackQuantity,
        shippedAfterEndDate: fallbackQuantity,
      });
    }
  }

  return splits;
};

export const getTotalOnHandAsOfEndDate = (inventory, endDate) => {
  return getStorageSplitsOnHandAsOfEndDate(inventory, endDate).reduce(
    (sum, split) => sum + (Number(split?.quantityOnHand) || 0),
    0,
  );
};

const calculateProratedDailyTierBreakdown = (
  billableDays,
  rates,
  service,
  billingUOM,
) => {
  if (!Number.isFinite(billableDays) || billableDays <= 0) return [];

  const normalizedRates = normalizeGraduatedRates(rates);
  const firstNdaysRate = getUnitPrice(service, billingUOM, null);
  if (!Number.isFinite(firstNdaysRate) || firstNdaysRate < 0) return null;

  if (!normalizedRates.length) return [];

  let remainingNdays = billableDays;
  const tierBreakdown = [];

  for (const [index, tier] of normalizedRates.entries()) {
    if (remainingNdays <= 0) break;

    const tierRate = index === 0 ? firstNdaysRate : tier.rate;
    if (!Number.isFinite(tierRate) || tierRate < 0) return null;

    const tierDays =
      tier.days == null ? remainingNdays : Math.min(remainingNdays, tier.days);

    if (tierDays > 0) {
      tierBreakdown.push({
        tierIndex: index,
        days: trimDecimal(tierDays),
        unitCost: trimDecimal(tierRate, 2),
        amountPerUnit: trimDecimal(tierDays * tierRate, 2),
      });
      remainingNdays -= tierDays;
    }
  }

  return tierBreakdown;
};

const scaleTierBreakdownAmounts = (
  tierBreakdown,
  billedQuantity,
  totalAmount,
) => {
  if (!Array.isArray(tierBreakdown) || !tierBreakdown.length) return [];

  const baseAmount = tierBreakdown.reduce(
    (sum, tier) => sum + (Number(tier?.amountPerUnit) || 0) * billedQuantity,
    0,
  );
  if (!Number.isFinite(baseAmount) || baseAmount <= 0) return [];

  const ratio = totalAmount / baseAmount;
  let allocatedAmount = 0;

  return tierBreakdown
    .map((tier, index) => {
      const isLastTier = index === tierBreakdown.length - 1;
      const rawTierAmount =
        (Number(tier?.amountPerUnit) || 0) * billedQuantity * ratio;
      const amount = isLastTier
        ? trimDecimal(totalAmount - allocatedAmount, 2)
        : trimDecimal(rawTierAmount, 2);
      allocatedAmount += amount;

      return {
        ...tier,
        billedQuantity: trimDecimal(billedQuantity),
        amount,
      };
    })
    .filter((tier) => (Number(tier?.amount) || 0) > 0);
};

/**
 * Calculate storage fees for inventory items using charge rules
 * @param {Object} inventory - Inventory item
 * @param {Object} billableItem - Billable item details
 * @param {Object} options - Additional options (startDate, endDate, etc.)
 * @param {Object} rule - Optional explicit charge rule override (preferred for line-level billing)
 * @returns {Object} Fee calculation result
 */
export const calculateStorageFees = (
  inventory,
  billableItem,
  options = {},
  rule,
) => {
  // Find applicable charge rule for this inventory (prefer explicit rule param)
  const applicableRule = rule || inventory.appliedRule;

  if (!applicableRule) {
    return { amount: "MISSING RULE", uom: "" };
  }

  // Get service and rate information
  const service = applicableRule.service;
  if (!service) {
    return { amount: "MISSING SERVICE", uom: applicableRule.billingUOM };
  }

  // Calculate quantity based on rule's billing UOM
  const quantity = calculateStorageQuantity(
    applicableRule,
    inventory,
    billableItem,
  );

  if (quantity === null || quantity <= 0) {
    return { amount: "MISSING QTY", uom: applicableRule.billingUOM };
  }

  let baseAmount = 0;
  let tierBreakdown = [];

  if (applicableRule.feeAccrual === FeeAccrual.PRORATED_DAILY) {
    const perUnitTierBreakdown = calculateProratedDailyTierBreakdown(
      Number(billableItem?.billableDays) || 0,
      applicableRule.graduatedRates,
      service,
      applicableRule.billingUOM,
    );

    if (perUnitTierBreakdown == null) {
      return {
        amount: "MISSING RATE",
        uom: applicableRule.billingUOM,
      };
    }

    if (perUnitTierBreakdown.length) {
      baseAmount =
        quantity *
        perUnitTierBreakdown.reduce(
          (sum, tier) => sum + (Number(tier?.amountPerUnit) || 0),
          0,
        );
    } else {
      const unitPrice = getUnitPrice(service, applicableRule.billingUOM);
      if (unitPrice === null) {
        return {
          amount: "MISSING RATE",
          uom: applicableRule.billingUOM,
        };
      }
      baseAmount =
        quantity * Number(billableItem?.billableDays || 0) * unitPrice;
    }
    tierBreakdown = perUnitTierBreakdown;
  } else {
    // Get unit price from service
    const unitPrice = getUnitPrice(service, applicableRule.billingUOM);
    if (unitPrice === null) {
      return {
        amount: "MISSING RATE",
        uom: applicableRule.billingUOM,
      };
    }

    // Calculate billable periods based on rule's fee accrual
    const billablePeriods = calculateBillablePeriods(
      applicableRule,
      billableItem,
    );
    // Calculate base amount
    baseAmount = quantity * unitPrice * billablePeriods;
  }

  // Apply min/max charge constraints
  const finalAmount = applyChargeConstraints(
    baseAmount,
    applicableRule.minCharge,
    applicableRule.maxCharge,
  );
  const finalTierBreakdown = scaleTierBreakdownAmounts(
    tierBreakdown,
    quantity,
    finalAmount,
  );

  return {
    amount: trimDecimal(finalAmount, 2),
    uom: applicableRule.billingUOM,
    billedQuantity: trimDecimal(quantity),
    tierBreakdown: finalTierBreakdown,
  };
};

/**
 * Calculate quantity for storage billing based on rule's billing UOM
 * @param {Object} rule - Charge rule
 * @param {Object} inventory - Inventory item
 * @param {Object} billableItem - Billable item details
 * @returns {number|null} Calculated quantity
 */
const calculateStorageQuantity = (rule, inventory, billableItem) => {
  // Handle per-unit billing (CAR, TL)
  if (
    inventory.inventoryUOM === Unit.CAR ||
    inventory.inventoryUOM === Unit.TL
  ) {
    return 1;
  }

  // Use billable item quantity if it matches the rule's billing UOM
  if (billableItem.inventoryUOM === rule.billingUOM) {
    return billableItem.quantity;
  }

  // Try to convert from inventory units to billing UOM
  // This is important for commodities like lumber where inventory is tracked in UNITS but billed in MBF
  const convertedQuantity = getConversionValue(
    inventory,
    billableItem.quantity,
    rule.billingUOM,
    billableItem.inventoryUOM,
    null,
  );

  return convertedQuantity;
};

/**
 * Calculate billable periods based on rule's fee accrual and options
 * @param {Object} rule - Charge rule
 * @param {Object} billableItem - Billable item details
 * @param {Object} options - Additional options
 * @returns {number} Billable periods
 */
export const calculateBillablePeriods = (rule, billableItem) => {
  if (
    rule.feeAccrual === FeeAccrual.FLAT_RATE ||
    rule.feeAccrual === FeeAccrual.FLAT_ONHAND
  ) {
    return 1; // Flat rate per billing period
  }

  // Daily prorated billing
  return billableItem.billableDays || 1;
};

export const calculateStorageFreeDays = (rule, receivedAt) => {
  if (!rule) return 0;

  let freeDays = rule?.freeDays || 0;

  // Supports fixed numeric days and dynamic policy: free until end of delivery month
  if (rule?.freeDaysPolicy === FreeDaysPolicy.END_OF_MONTH) {
    const received = startOfDay(new Date(receivedAt));
    const startOfNextMonth = startOfMonth(addMonths(received, 1));
    // Number of days from received (start-of-day) up to the first day of next month
    // This makes billing start on the first day of the next month
    freeDays = Math.max(0, differenceInDays(startOfNextMonth, received));
  }

  return Number(freeDays) || 0;
};

const getStoredQuantityOnHandFromInventoryItems = (inventory) => {
  const items = inventory?.inventoryItems?.items || [];
  const storedItems = items.filter(
    (ii) => ii?.status === InventoryStatus.STORED,
  );
  if (!storedItems.length) return 0;
  return storedItems.reduce(
    (sum, ii) => sum + (Number(ii?.quantityOnHand) || 0),
    0,
  );
};

const getStorageSplitsFromInventory = (inventory) => {
  const inventoryItems = inventory?.inventoryItems?.items || [];

  const splits = inventoryItems
    .filter((ii) => ii?.status === InventoryStatus.STORED)
    .map((ii) => ({
      inventoryItemId: ii?.id ?? null,
      areaId: ii?.areaId ?? inventory?.areaId ?? null,
      zoneId: ii?.zoneId ?? inventory?.zoneId ?? null,
      quantityOnHand: Number(ii?.quantityOnHand) || 0,
    }))
    .filter((s) => s.quantityOnHand > 0);

  // Fallback: if we have no inventoryItems OR all stored splits are zero, treat inventory as single split.
  if (!inventoryItems.length || splits.length === 0) {
    const invQtyOnHand = Number(inventory?.quantityOnHand);
    return [
      {
        inventoryItemId: null,
        areaId: inventory?.areaId ?? null,
        zoneId: inventory?.zoneId ?? null,
        quantityOnHand: Number.isFinite(invQtyOnHand) ? invQtyOnHand : 0,
      },
    ];
  }

  return splits;
};

const allocateQuantityAcrossSplits = (totalQuantity, splits) => {
  const qty = Number(totalQuantity) || 0;
  if (!splits?.length || qty <= 0) return [];

  const totalSplitQty = splits.reduce(
    (sum, s) => sum + (Number(s.quantityOnHand) || 0),
    0,
  );
  const denom = totalSplitQty > 0 ? totalSplitQty : splits.length;

  let remaining = qty;
  return splits.map((s, idx) => {
    const isLast = idx === splits.length - 1;
    const splitWeight = totalSplitQty > 0 ? Number(s.quantityOnHand) || 0 : 1;
    const allocated = isLast
      ? remaining
      : trimDecimal((qty * splitWeight) / denom, 6);
    remaining = trimDecimal(remaining - allocated, 6);
    return { ...s, allocatedQuantity: allocated };
  });
};

const getShippedStorageLineItems = (shipmentLineItems, startDate, endDate) => {
  const start = toBoundaryDate(startDate);
  const end = toBoundaryDate(endDate);

  return shipmentLineItems
    .filter((item) => item?.status === ShipmentLineItemStatus.SHIPPED)
    .map((item) => {
      const shippedDate = getShipmentDate(item);
      const shippedAt = toDateSafe(shippedDate);
      return { item, shippedDate, shippedAt };
    })
    .filter(({ shippedAt }) => shippedAt && start && end && shippedAt >= start)
    .map(({ item, shippedDate, shippedAt }) => ({
      item,
      shippedDate: shippedAt < end ? shippedDate : "",
    }));
};

const buildStorageSplitEntries = (kind, splits) =>
  splits.map((split) => ({
    kind,
    shipmentLineItemId: null,
    shipmentLineItemInventoryItemId: null,
    inventoryItemId: split?.inventoryItemId ?? null,
    quantity: Number(split?.quantityOnHand) || 0,
    shippedDate: "",
  }));

const buildStorageQuantityEntry = (kind, quantity) => ({
  kind,
  quantity: Number(quantity) || 0,
  shippedDate: "",
});

const getStorageBillableEntriesByInventoryItem = (inventory, options) => {
  const { startDate, endDate } = options;
  const shipmentLineItems = inventory?.shipmentLineItems?.items || [];

  const entries = [];

  const pushShipmentEntries = (lineItem, shippedDate) => {
    const sliInventoryItems = lineItem?.inventoryItems?.items || [];
    sliInventoryItems.forEach((slii) => {
      entries.push({
        kind: StorageEntryKind.SHIPPED,
        shipmentLineItemId: lineItem?.id ?? null,
        shipmentLineItemInventoryItemId: slii?.id ?? null,
        inventoryItemId: slii?.inventoryItemId ?? null,
        quantity: Number(slii?.quantityToPick) || 0,
        shippedDate,
      });
    });
  };

  // STORED: bill shipped quantities during the period + bill remaining on-hand through end date
  if (inventory?.status === InventoryStatus.STORED) {
    getShippedStorageLineItems(shipmentLineItems, startDate, endDate).forEach(
      ({ item, shippedDate }) => {
        pushShipmentEntries(item, shippedDate);
      },
    );

    // Remaining on-hand NOW (used by non-FLAT accruals)
    const splits = getStorageSplitsFromInventory(inventory);
    entries.push(
      ...buildStorageSplitEntries(StorageEntryKind.ONHAND_CURRENT, splits),
    );

    // ON-HAND AS-OF endDate — always generated so FLAT_ONHAND can pick the
    // reconstructed quantity even when inventory-level timestamps are still null
    // (partially shipped STORED inventory where individual SLIs shipped after boundary).
    const asOfSplits = getStorageSplitsOnHandAsOfEndDate(inventory, endDate);
    entries.push(
      ...buildStorageSplitEntries(
        StorageEntryKind.ONHAND_ASOF_ENDDATE,
        asOfSplits,
      ),
    );

    return entries;
  }

  // SHIPPED: bill shipped quantities during the period + (for FLAT_ONHAND) on-hand AS-OF endDate
  if (inventory?.status === InventoryStatus.SHIPPED) {
    getShippedStorageLineItems(shipmentLineItems, startDate, endDate).forEach(
      ({ item, shippedDate }) => {
        pushShipmentEntries(item, shippedDate);
      },
    );

    // Add ONHAND_ASOF_ENDDATE entries when inventory was on-hand at endDate
    if (wasStoredAsOfEndDate(inventory, endDate)) {
      const asOfSplits = getStorageSplitsOnHandAsOfEndDate(inventory, endDate);
      entries.push(
        ...buildStorageSplitEntries(
          StorageEntryKind.ONHAND_ASOF_ENDDATE,
          asOfSplits,
        ),
      );
    }

    return entries;
  }

  return entries;
};

const getStorageBillableEntries = (inventory, options) => {
  const { startDate, endDate } = options;
  const shipmentLineItems = inventory?.shipmentLineItems?.items || [];

  if (inventory?.status === InventoryStatus.STORED) {
    if (!shipmentLineItems.length) {
      return [
        buildStorageQuantityEntry(
          StorageEntryKind.ONHAND_CURRENT,
          inventory?.quantityOnHand,
        ),
      ];
    }

    const entries = getShippedStorageLineItems(
      shipmentLineItems,
      startDate,
      endDate,
    ).map(({ item, shippedDate }) => ({
      kind: StorageEntryKind.SHIPPED,
      quantity: Number(item?.totalQuantityToPick) || 0,
      shippedDate,
    }));

    // Remaining on-hand NOW (used by non-FLAT accruals)
    entries.push(
      buildStorageQuantityEntry(
        StorageEntryKind.ONHAND_CURRENT,
        inventory?.inventoryItems?.items?.length
          ? getStoredQuantityOnHandFromInventoryItems(inventory)
          : inventory?.quantityOnHand,
      ),
    );

    // ON-HAND AS-OF endDate — always generated so FLAT_ONHAND picks the
    // reconstructed quantity even for partially shipped STORED inventory
    // where inventory-level timestamps are still null.
    entries.push(
      buildStorageQuantityEntry(
        StorageEntryKind.ONHAND_ASOF_ENDDATE,
        getTotalOnHandAsOfEndDate(inventory, endDate),
      ),
    );

    return entries;
  }

  if (inventory?.status === InventoryStatus.SHIPPED) {
    const entries = getShippedStorageLineItems(
      shipmentLineItems,
      startDate,
      endDate,
    ).map(({ item, shippedDate }) => ({
      kind: StorageEntryKind.SHIPPED,
      quantity: Number(item?.totalQuantityToPick) || 0,
      shippedDate,
    }));

    // For FLAT_ONHAND, billing is based on on-hand snapshot at endDate (if it was on hand then)
    if (wasStoredAsOfEndDate(inventory, endDate)) {
      entries.push(
        buildStorageQuantityEntry(
          StorageEntryKind.ONHAND_ASOF_ENDDATE,
          getTotalOnHandAsOfEndDate(inventory, endDate),
        ),
      );
    }

    return entries;
  }

  return [];
};

/**
 * Check if inventory was stored as of the end date.
 * Billable for FLAT_ONHAND iff inventory was still on hand at close of billing period (endDate).
 * STORED now => was on hand at endDate; SHIPPED but shipped after endDate => was on hand at endDate.
 * @param {Object} inv - Inventory object
 * @param {string} endDate - End date
 * @returns {boolean} True if inventory was stored as of the end date
 */
const wasStoredAsOfEndDate = (inv, endDate) => {
  return wasInventoryStoredAsOfEndDate(inv, endDate);
};

/**
 * Per-inventory processing with rule-specific freeDays
 * - Determine the most specific applicable rule for each inventory
 * - If the inventory is still within the rule's freeDays window as of the report end date,
 *   exclude it from billing for this period
 * - Otherwise, compute billable items and fees using that rule
 *
 * @param {Object} params - Processing parameters
 * @param {Array} params.inventories - Array of inventory items
 * @param {Array} params.storageRules - Array of storage charge rules
 * @param {Object} params.options - Additional options
 * @param {Function} params.setFinalData - Setter for final data
 * @param {Function} params.setUnitTotals - Setter for unit totals
 * @param {Function} params.setHasWeight - Setter for weight flag
 * @returns {void}
 */

// #########################################################
//        STORAGE BILLING: ORCHESTRATOR HELPERS
// #########################################################

const classifyBillingPath = (inv, storageRules) => {
  const rule = findApplicableRule(inv, storageRules);
  return { rule, isShipmentLevel: !!rule?.carrierType };
};

/**
 * When a shipment's storage ended: the day the car (or trailer) left the
 * yard. Shipment-level storage bills the vehicle, not the product, so the
 * inventory's own depletedAt is beside the point — a car unloaded to ground
 * and pulled stops billing per car even though its stock is still stored.
 *
 * `collectedDateTime` is written when the car is pulled in Track Management
 * and is the departure when present. `releaseDateTime` only says the customer
 * released the car, and the pull can follow days later, so it is the fallback
 * for cars released but not yet pulled — and for cars pulled before collection
 * was tracked. It only counts once the shipment is RELEASED: imports stamp a
 * release date at creation, and a scheduled release is not a departure.
 *
 * Returns the raw value (for depletedAt / dateShipped) alongside its parsed
 * Date, plus whether the shipment is RELEASED so a released shipment with no
 * usable date can be refused rather than billed through the period end.
 */
const getShipmentDeparture = (shipment) => {
  const released = shipment?.status === InboundStatus.RELEASED;
  const collectedAt = toDateSafe(shipment?.collectedDateTime);
  const releasedAt = released ? toDateSafe(shipment?.releaseDateTime) : null;

  const departureDateTime = collectedAt
    ? shipment.collectedDateTime
    : releasedAt
      ? shipment.releaseDateTime
      : null;

  return { released, departedAt: collectedAt || releasedAt, departureDateTime };
};

/**
 * Free-car slots for per-vehicle rules that carry `freeCars`.
 *
 * A rule with freeCars = N lets the customer keep N vehicles on the track at
 * no charge at any one time; the N+1th and later bill after their own free
 * days. The slots belong to the earliest-placed vehicles still on hand, and
 * when one of those leaves, the next-placed vehicle inherits the slot and
 * stops billing from the following day. A vehicle only ever moves up that
 * queue as the vehicles ahead of it leave, so each one bills for a single
 * contiguous stretch — from the end of its free days until its slot opens —
 * which is what the per-vehicle builder caps its billable days on.
 *
 * Returns Map<inboundShipmentId, Date|null>: the local-midnight day the
 * vehicle holds a slot from — its placement day when a slot was open on
 * arrival — or null when none opens before the period ends. Vehicles under a
 * rule without freeCars are absent, and the builder leaves them alone.
 *
 * Candidates mirror the two report paths. Inventory rows whose winning rule
 * is per-vehicle count once per shipment off their first row, as Path A
 * bills them; shipments from the shipment read count when no inventory row
 * of theirs resolved to an inventory-level rule, under the rule the shipment
 * fallback would bill them by (resolvePerCarRule). Slots are per rule, so
 * RAIL and TRUCK rules keep separate allowances. Vehicles gone before the
 * period hold no slot in it, and a RELEASED vehicle with no departure date —
 * which the builder refuses to bill — is left out rather than assumed to
 * still be on the track.
 */
const allocateFreeCarSlots = ({
  inventories,
  storageShipments,
  leasedZoneIds,
  storageRules,
  options,
}) => {
  const slotOpensById = new Map();
  const startBoundary = toBoundaryDate(options?.startDate);
  const endBoundary = toBoundaryDate(options?.endDate);
  if (!startBoundary || !endBoundary) return slotOpensById;

  const perCarRules = (storageRules || []).filter((r) => r?.carrierType);
  if (!perCarRules.some((r) => Number(r.freeCars) > 0)) return slotOpensById;

  const candidates = new Map();
  const inventoryRuledShipmentIds = new Set();

  const addCandidate = (shipmentId, rule, placedAt, shipment) => {
    if (!shipmentId || !rule || candidates.has(shipmentId)) return;
    const placed = toDateSafe(placedAt);
    if (!placed) return;
    const departure = getShipmentDeparture(shipment);
    if (departure.released && !departure.departedAt) return;
    if (departure.departedAt && departure.departedAt < startBoundary) return;
    const placedDay = startOfDay(placed);
    if (placedDay >= endBoundary) return;
    candidates.set(shipmentId, {
      shipmentId,
      ruleId: rule.id,
      freeCars: Number(rule.freeCars) || 0,
      placedDay,
      departDay: departure.departedAt ? startOfDay(departure.departedAt) : null,
    });
  };

  (inventories || []).forEach((inv) => {
    if (!inv?.inboundShipmentId) return;
    const { rule, isShipmentLevel } = classifyBillingPath(inv, storageRules);
    if (!isShipmentLevel) {
      // Entries decide this, not the header-level rule, because that is what
      // the billing pass decides on (see the same test in the inventories
      // loop below). Testing the rule instead misses both ways: a header rule
      // matches while a zero-quantity row builds no entries, and the SIT
      // fallback then bills that car per railcar with no slot allocated to
      // it; or the header rule is null while the splits still bill, and a car
      // the fallback never touches consumes a slot another car needed.
      if (buildBillableEntries(inv, storageRules, options).length > 0) {
        inventoryRuledShipmentIds.add(inv.inboundShipmentId);
      }
      return;
    }
    addCandidate(
      inv.inboundShipmentId,
      rule,
      inv.receivedAt,
      inv.inboundShipment,
    );
  });

  (storageShipments || []).forEach((shipment) => {
    if (!shipment?.id || inventoryRuledShipmentIds.has(shipment.id)) return;
    if (shipment.zoneId && leasedZoneIds?.has(shipment.zoneId)) return;
    addCandidate(
      shipment.id,
      resolvePerCarRule(shipment, storageRules),
      shipment.placedDateTime,
      shipment,
    );
  });

  const carsByRule = new Map();
  candidates.forEach((car) => {
    if (!(car.freeCars > 0)) return;
    if (!carsByRule.has(car.ruleId)) carsByRule.set(car.ruleId, []);
    carsByRule.get(car.ruleId).push(car);
  });

  carsByRule.forEach((cars) => {
    cars.sort(
      (a, b) =>
        a.placedDay - b.placedDay ||
        String(a.shipmentId).localeCompare(String(b.shipmentId)),
    );
    cars.forEach((car, index) => {
      // Vehicles placed before this one and still on the track the day it
      // arrived. Only they stand between it and a slot.
      const ahead = cars
        .slice(0, index)
        .filter(
          (other) => !other.departDay || other.departDay >= car.placedDay,
        );
      if (ahead.length < car.freeCars) {
        slotOpensById.set(car.shipmentId, car.placedDay);
        return;
      }
      // Enough of them have to leave for the count ahead to drop below the
      // allowance; the slot opens the day after that departure.
      const departures = ahead
        .map((other) => other.departDay)
        .filter(Boolean)
        .sort((a, b) => a - b);
      const opensAfter = departures[ahead.length - car.freeCars];
      slotOpensById.set(
        car.shipmentId,
        opensAfter ? addDays(opensAfter, 1) : null,
      );
    });
  });

  return slotOpensById;
};

/**
 * Build the single qty=1 billable line for a shipment billed per vehicle.
 *
 * Two kinds of caller. Event-driven invoicing (InvoiceUtils) bills a window
 * it has already closed and passes that window's end as `shippedDate`; the
 * vehicle's departure has nothing to add there. The periodic storage report
 * — tracked inventory under a carrierType rule and the shipment fallback alike —
 * leaves `shippedDate` out, and the line then ends when the vehicle left: a
 * car bills through the day it left the track (or the period end, whichever
 * is first), counts as on hand for FLAT_ONHAND only while still on the
 * track, and bills nothing once it left before the period started.
 *
 * `freeSlotOpensAt` (report path only) is the day the vehicle holds one of
 * the rule's free-car slots from, as allocated by allocateFreeCarSlots: the
 * line bills nothing when that day comes before billing would start, and a
 * PRORATED line otherwise bills only up to it. Flat accruals have no partial
 * period, so a vehicle billable for any day outside the slots bills in full.
 */
export const buildShipmentLevelStorageBillableItem = (
  inventory,
  rule,
  { startDate, endDate, shippedDate, freeSlotOpensAt } = {},
) => {
  if (!inventory?.receivedAt || !rule || !endDate) return null;

  const startBoundary = toBoundaryDate(startDate);
  const departure =
    shippedDate === undefined
      ? getShipmentDeparture(inventory.inboundShipment)
      : null;

  if (departure) {
    // A released shipment with no usable date has no end to its storage
    // window, so it is refused rather than billed through the period end.
    if (departure.released && !departure.departedAt) return null;

    // Left the yard before the period started — no storage in this period.
    if (
      departure.departedAt &&
      startBoundary &&
      departure.departedAt < startBoundary
    ) {
      return null;
    }
  }

  const dateShipped = departure
    ? departure.departureDateTime || ""
    : shippedDate;

  const endBoundary = toBoundaryDate(endDate);
  const freeDays = calculateStorageFreeDays(rule, inventory.receivedAt);
  // Counted through the day the vehicle left, not the period end: a FLAT
  // vehicle pulled inside its free days owes nothing for the period.
  const daysStored = countStorageDays(
    inventory.receivedAt,
    dateShipped,
    endDate,
  );

  if (daysStored <= freeDays) return null;

  // Billing ends at the vehicle's departure unless a free-car slot opens
  // first; the physical departure stays on the line for display and the
  // on-hand test below.
  let billingCutoff = dateShipped;
  const slotOpens = departure ? toDateSafe(freeSlotOpensAt) : null;
  if (slotOpens) {
    const firstBillingDay = addDays(
      startOfDay(toDateSafe(inventory.receivedAt)),
      freeDays,
    );
    const billingStart =
      startBoundary && startBoundary > firstBillingDay
        ? startBoundary
        : firstBillingDay;
    if (slotOpens <= billingStart) return null;
    if (
      rule.feeAccrual === FeeAccrual.PRORATED_DAILY &&
      slotOpens < endBoundary
    ) {
      const lastBillableDay = addDays(slotOpens, -1);
      const shippedAt = toDateSafe(dateShipped);
      if (!shippedAt || lastBillableDay < shippedAt) {
        billingCutoff = lastBillableDay;
      }
    }
  }

  // On hand at the period end means the vehicle was still here, whatever the
  // product's status says. (A closed invoice window ends on its own end, so
  // it always reads as on hand.)
  if (
    rule.feeAccrual === FeeAccrual.FLAT_ONHAND &&
    dateShipped &&
    !isOnOrAfterBoundary(dateShipped, endBoundary)
  ) {
    return null;
  }

  const accrual = rule.feeAccrual;
  const shipmentBillableItem = {
    quantity: 1,
    inventoryUOM: rule.billingUOM,
    weight: 0,
    weightUOM: null,
    dateShipped,
    billableDays: calculateBillableDays(
      startDate,
      endDate,
      inventory.receivedAt,
      billingCutoff,
      accrual,
      freeDays,
    ),
  };

  // A PRORATED vehicle that left inside its free window has no billable days
  // in the period; there is no line to show for it. Invoice windows are left
  // alone: a same-day FLAT_RATE window has zero days and still bills.
  if (departure && !(Number(shipmentBillableItem.billableDays) > 0)) {
    return null;
  }

  const fees = calculateStorageFees(
    inventory,
    shipmentBillableItem,
    { startDate, endDate, freeDays, accrual },
    rule,
  );
  shipmentBillableItem.fees = fees.amount;
  shipmentBillableItem.billableUom = fees.uom;
  shipmentBillableItem.billedQuantity = fees.billedQuantity;
  shipmentBillableItem.tierBreakdown = fees.tierBreakdown || [];
  shipmentBillableItem.appliedRule = rule;

  return shipmentBillableItem;
};

/**
 * Shipment-level billing (Path A): bill qty=1 per inbound shipment.
 * Returns null to skip entirely, [] for dedup (include in report with no items),
 * or [billableItem] for normal billing.
 */
const processShipmentLevelBilling = (
  inv,
  rule,
  options,
  billedShipments,
  endBoundary,
) => {
  // Inventory.inboundShipmentId is ID! and the storage report selects it, so
  // it is the key both this pass and the shipment fallback dedup on. A composite
  // fallback key here would never agree with the ids the SIT pass tests.
  const shipmentKey = inv.inboundShipmentId;

  if (billedShipments.has(shipmentKey)) return [];

  const shipmentBillableItem = buildShipmentLevelStorageBillableItem(
    inv,
    rule,
    {
      startDate: options.startDate,
      endDate: options.endDate,
      freeSlotOpensAt: options.freeSlotOpensById?.get(shipmentKey),
    },
  );
  if (!shipmentBillableItem) return null;

  billedShipments.add(shipmentKey);
  return [shipmentBillableItem];
};

/**
 * Accrual-specific entry filter. Returns true if the entry should be billed
 * under the given accrual type.
 */
const shouldBillEntry = (entryKindRaw, accrual, allEntries) => {
  const entryKind = normalizeStorageEntryKind(entryKindRaw, accrual);

  if (accrual === FeeAccrual.FLAT_ONHAND) {
    if (entryKind !== StorageEntryKind.ONHAND_ASOF_ENDDATE) return false;
    if (
      entryKindRaw === StorageEntryKind.ONHAND_CURRENT &&
      allEntries.some((e) => e.kind === StorageEntryKind.ONHAND_ASOF_ENDDATE)
    ) {
      return false;
    }
    return true;
  }

  if (entryKind === StorageEntryKind.ONHAND_ASOF_ENDDATE) return false;
  return true;
};

/**
 * Resolve billable entries for inventory-level billing.
 * Dispatches between inventoryItem-level joins (Path B) and
 * split-based fallback (Path C). Returns a flat array of resolved entries,
 * each carrying its own rule, freeDays, and location context.
 */
const buildBillableEntries = (inv, storageRules, options) => {
  const hasInventoryItemJoins =
    (inv?.shipmentLineItems?.items || []).some(
      (sli) => (sli?.inventoryItems?.items || []).length > 0,
    ) && (inv?.inventoryItems?.items || []).length > 0;

  if (hasInventoryItemJoins) {
    return buildEntriesFromInventoryItems(inv, storageRules, options);
  }

  return buildEntriesFromSplits(inv, storageRules, options);
};

const buildEntriesFromInventoryItems = (inv, storageRules, options) => {
  const inventoryItemById = new Map(
    (inv?.inventoryItems?.items || [])
      .filter((ii) => ii?.id)
      .map((ii) => [
        ii.id,
        {
          areaId: ii?.areaId ?? null,
          zoneId: ii?.zoneId ?? null,
          quantityOnHand: Number(ii?.quantityOnHand) || 0,
        },
      ]),
  );

  const rawEntries = getStorageBillableEntriesByInventoryItem(
    inv,
    options,
  ).filter((e) => (Number(e?.quantity) || 0) > 0);

  return rawEntries.map((entry) => {
    const invItem = inventoryItemById.get(entry.inventoryItemId);
    const areaId = invItem?.areaId ?? inv?.areaId ?? null;
    const zoneId = invItem?.zoneId ?? inv?.zoneId ?? null;

    const recordForRule = { ...inv, areaId, zoneId };
    const rule = findApplicableRule(recordForRule, storageRules);
    const freeDays = calculateStorageFreeDays(rule, inv.receivedAt);

    return {
      quantity: entry.quantity,
      areaId,
      zoneId,
      rule,
      freeDays,
      kind:
        entry?.kind ||
        (entry?.shipmentLineItemId != null
          ? StorageEntryKind.SHIPPED
          : StorageEntryKind.ONHAND_CURRENT),
      shippedDate: entry.shippedDate || "",
      inventoryItemId: entry.inventoryItemId ?? null,
      shipmentLineItemId: entry.shipmentLineItemId ?? null,
      shipmentLineItemInventoryItemId:
        entry.shipmentLineItemInventoryItemId ?? null,
    };
  });
};

const buildEntriesFromSplits = (inv, storageRules, options) => {
  const baseSplits = getStorageSplitsFromInventory(inv);
  const splitContexts = baseSplits.map((split) => {
    const recordForRule = {
      ...inv,
      areaId: split.areaId,
      zoneId: split.zoneId,
    };
    const rule = findApplicableRule(recordForRule, storageRules);
    const freeDays = calculateStorageFreeDays(rule, inv.receivedAt);
    return { ...split, rule, freeDays };
  });

  const endDateSplitsRaw = getStorageSplitsOnHandAsOfEndDate(
    inv,
    options.endDate,
  );
  const endDateSplitContexts = endDateSplitsRaw.map((split) => {
    const recordForRule = {
      ...inv,
      areaId: split.areaId,
      zoneId: split.zoneId,
    };
    const rule = findApplicableRule(recordForRule, storageRules);
    const freeDays = calculateStorageFreeDays(rule, inv.receivedAt);
    return { ...split, rule, freeDays };
  });

  const ensureAllocSplits = (entry) => {
    if (entry?.kind === StorageEntryKind.ONHAND_ASOF_ENDDATE) {
      const sumAsOf = endDateSplitContexts.reduce(
        (sum, s) => sum + (Number(s.quantityOnHand) || 0),
        0,
      );
      if (sumAsOf > 0) return endDateSplitContexts;
    }

    const splitQtySum = splitContexts.reduce(
      (sum, s) => sum + (Number(s.quantityOnHand) || 0),
      0,
    );
    if (splitQtySum > 0) return splitContexts;

    const fallbackRule = findApplicableRule(inv, storageRules);
    return [
      {
        inventoryItemId: null,
        areaId: inv?.areaId ?? null,
        zoneId: inv?.zoneId ?? null,
        quantityOnHand: Number(entry?.quantity) || 0,
        rule: fallbackRule,
        freeDays: calculateStorageFreeDays(fallbackRule, inv.receivedAt),
      },
    ];
  };

  const rawEntries = getStorageBillableEntries(inv, options);
  const resolvedEntries = [];

  rawEntries.forEach((entry) => {
    if (!entry?.quantity) return;

    const entryKind =
      entry?.kind ||
      (entry?.shippedDate
        ? StorageEntryKind.SHIPPED
        : StorageEntryKind.ONHAND_CURRENT);

    const allocSplits = ensureAllocSplits(entry);
    const allocations = allocateQuantityAcrossSplits(
      entry.quantity,
      allocSplits,
    );

    allocations.forEach((a) => {
      const allocatedQty = Number(a?.allocatedQuantity) || 0;
      if (allocatedQty <= 0) return;

      resolvedEntries.push({
        quantity: allocatedQty,
        areaId: a?.areaId ?? null,
        zoneId: a?.zoneId ?? null,
        rule: a?.rule || null,
        freeDays: Number(a?.freeDays) || 0,
        kind: entryKind,
        shippedDate: entry?.shippedDate || "",
        inventoryItemId: a?.inventoryItemId ?? null,
        shipmentLineItemId: null,
        shipmentLineItemInventoryItemId: null,
      });
    });
  });

  return resolvedEntries;
};

/**
 * Process a single resolved storage entry. Returns a billableItem or null to skip.
 * Handles all accrual types (FLAT_RATE, FLAT_ONHAND, PRORATED_DAILY) via shouldBillEntry.
 */
const processStorageEntry = (inv, entry, allEntries, options) => {
  const { rule, freeDays, areaId, zoneId } = entry;
  const accrual = rule?.feeAccrual;

  if (!shouldBillEntry(entry.kind, accrual, allEntries)) return null;

  // shippedDate is set only when the entry left inside the period; on-hand
  // entries count through the period end.
  const daysStoredForEntry = countStorageDays(
    inv.receivedAt,
    entry.shippedDate,
    options.endDate,
  );

  if (rule && daysStoredForEntry <= freeDays) return null;

  if (
    accrual === FeeAccrual.FLAT_ONHAND &&
    !wasStoredAsOfEndDate(inv, options.endDate)
  ) {
    return null;
  }

  const inventoryForCalc = { ...inv, areaId, zoneId, appliedRule: rule };

  const billableItem = createBillableItem(
    inventoryForCalc,
    entry.quantity,
    entry.shippedDate,
    {
      ...options,
      freeDays: rule ? freeDays : 0,
      accrual,
    },
  );

  billableItem.inventoryItemId = entry.inventoryItemId;
  billableItem.shipmentLineItemId = entry.shipmentLineItemId;
  billableItem.shipmentLineItemInventoryItemId =
    entry.shipmentLineItemInventoryItemId;
  billableItem.areaId = areaId;
  billableItem.zoneId = zoneId;
  billableItem.appliedRule = rule;

  if (!rule) {
    billableItem.fees = "MISSING RULE";
    billableItem.billableUom = "";
    return billableItem;
  }

  const fees = calculateStorageFees(
    inventoryForCalc,
    billableItem,
    { ...options, freeDays },
    rule,
  );
  billableItem.fees = fees.amount;
  billableItem.billableUom = fees.uom;
  billableItem.billedQuantity = fees.billedQuantity;
  billableItem.tierBreakdown = fees.tierBreakdown || [];

  return billableItem;
};

/**
 * Fill the row-level day and UOM columns the grid and the CSV read. Every
 * billing path lands here so that two cars stored the same way show the same
 * numbers: totalDaysStored is the time in storage up to the period end (or the
 * date it left, whichever came first), and billableDaysTotal is that minus the
 * free days the applied rules granted. Per-period billed days stay on the
 * billable items, which is where the CSV's Days column reads them.
 */
const computeInventorySummary = (inv, options) => {
  const freeDaysForDisplay = Math.min(
    ...(inv.billableItems || [])
      .map((bi) => calculateStorageFreeDays(bi?.appliedRule, inv.receivedAt))
      .filter((d) => Number.isFinite(d)),
  );

  if (inv.status === InventoryStatus.STORED) {
    inv.totalDaysStored = countStorageDays(
      inv.receivedAt,
      null,
      options.endDate,
    );
  } else if (inv.status === InventoryStatus.SHIPPED) {
    inv.totalDaysStored = countStorageDays(
      inv.receivedAt,
      inv.depletedAt,
      options.endDate,
    );
  }

  // A row with no billable items bills nothing - a dedup row for a car already
  // billed on another line, say - so it has no billable days either. Without
  // this the empty free-days list would read as zero free days and the row
  // would claim every stored day as billable.
  inv.billableDaysTotal = !(inv.billableItems || []).length
    ? 0
    : Math.max(
        0,
        (inv.totalDaysStored || 0) -
          (Number.isFinite(freeDaysForDisplay) ? freeDaysForDisplay : 0),
      );

  const uoms = Array.from(
    new Set(
      (inv.billableItems || []).map((bi) => bi?.billableUom).filter(Boolean),
    ),
  );
  if (uoms.length === 1) inv.billableUom = uoms[0];
};

/**
 * Add one billable line to the report's totals, kept per billing UOM. A line
 * the rules could not price carries its fee as text (MISSING RULE / SERVICE /
 * QTY / RATE), which turns that UOM's total into that label rather than
 * quietly understating it.
 */
const addLineToRunningTotals = (billableItem, runningTotalFees) => {
  const uom = billableItem?.billableUom;
  if (!uom) return;

  runningTotalFees[uom] = getStorageSumTotal(
    runningTotalFees,
    uom,
    billableItem.fees,
  );
};

const accumulateRunningTotals = (inv, billableItem, runningTotalFees) => {
  inv.totalFees = getTotalStorageFee(inv, billableItem);
  addLineToRunningTotals(billableItem, runningTotalFees);
};

const SYNTHETIC_BILLING_SOURCES = new Set(Object.values(BillingSource));

/**
 * True for a storage report row that was built from something other than an
 * Inventory record. Callers use it to withhold anything that would follow the
 * row's id back to an inventory - the id of a synthetic row is a composite of
 * its source and the billing period, not an inventory id.
 */
export const isSyntheticBillingRow = (record) =>
  SYNTHETIC_BILLING_SOURCES.has(record?.billingSource);

const buildLeasedZoneBillableItem = (zone, storageRules, options) => {
  const quantity = Number(zone?.leasedArea) || 0;
  if (quantity <= 0 || !zone?.leasedAreaUOM || !zone?.id) return null;

  const syntheticInventory = {
    id: zone.id,
    areaId: zone.areaId ?? null,
    zoneId: zone.id,
    receivedAt: options.startDate,
    inventoryUOM: zone.leasedAreaUOM,
    weight: 0,
    weightUOM: null,
  };
  const rule = findApplicableRule(syntheticInventory, storageRules);
  const freeDays = rule ? calculateStorageFreeDays(rule, options.startDate) : 0;
  const accrual = rule?.feeAccrual || FeeAccrual.FLAT_RATE;
  const billableDays = calculateBillableDays(
    options.startDate,
    options.endDate,
    options.startDate,
    "",
    accrual,
    freeDays,
  );
  const billableItem = {
    quantity,
    inventoryUOM: zone.leasedAreaUOM,
    weight: 0,
    weightUOM: null,
    dateShipped: "",
    billableDays,
    areaId: zone.areaId ?? null,
    zoneId: zone.id,
    lineGroupKey: zone.id,
    serviceNote: `Leased Area - ${zone.area?.name ? `${zone.area.name} / ` : ""} ${quantity} ${zone.leasedAreaUOM}`,
    appliedRule: rule || null,
  };

  if (!rule) {
    billableItem.fees = "MISSING RULE";
    billableItem.billableUom = "";
    billableItem.billedQuantity = quantity;
    billableItem.tierBreakdown = [];
    return billableItem;
  }

  const fees = calculateStorageFees(
    { ...syntheticInventory, appliedRule: rule },
    billableItem,
    {
      startDate: options.startDate,
      endDate: options.endDate,
      freeDays,
      accrual,
    },
    rule,
  );
  billableItem.fees = fees.amount;
  billableItem.billableUom = fees.uom;
  billableItem.billedQuantity = fees.billedQuantity;
  billableItem.tierBreakdown = fees.tierBreakdown || [];

  return billableItem;
};

const buildLeasedZoneBillableRecord = (zone, storageRules, options) => {
  const billableItem = buildLeasedZoneBillableItem(zone, storageRules, options);
  if (!billableItem) return null;

  return {
    id: `${zone.id}-${options.startDate}-${options.endDate}`,
    billingSource: BillingSource.LEASED_ZONE,
    receivedAt: `${options.startDate}T00:00:00`,
    productCode: "",
    productName: "Leased Area",
    poNumber: zone.areaName || "",
    railCarNumber: zone.name || "",
    quantityOnHand: Number(zone?.leasedArea) || 0,
    inventoryUOM: zone.leasedAreaUOM,
    weight: 0,
    weightUOM: null,
    areaId: zone.areaId ?? null,
    zoneId: zone.id,
    billableItems: [billableItem],
    totalFees: billableItem.fees,
    totalDaysStored: billableItem.billableDays,
    billableDaysTotal: billableItem.billableDays,
    billableUom: billableItem.billableUom || null,
  };
};

/**
 * The per-car rule a shipment bills under in the shipment fallback, or null
 * when it must not bill there.
 *
 * A SIT car matches the per-car (carrierType) rules alone. It is billed per
 * railcar, and an inventory-level rule cannot bill a car that has no inventory
 * quantities to bill — matching the full list and rejecting a non-per-car
 * winner would leave the car unbilled whenever a more specific inventory-level
 * rule outranks the per-car rule.
 *
 * A tracked (non-SIT) car resolves against every rule, the way its Inventory
 * rows would have. It reaches the fallback only when those rows fell outside
 * the report window — stock depleted before the period, car pulled inside it —
 * and bills per car only when a per-car rule would have won for the rows too.
 * Stock that bills per unit under a more specific inventory rule never grows a
 * per-car charge for the days its empty car waited to be pulled, which is the
 * outcome the inventory pass reaches when the rows are in the window.
 */
const resolvePerCarRule = (shipment, storageRules) => {
  const rules = storageRules || [];
  const candidates = shipment?.sit
    ? rules.filter((rule) => rule?.carrierType)
    : rules;
  const rule = findApplicableRule(shipment, candidates);
  return rule?.carrierType ? rule : null;
};

const vehicleStorageLabel = (shipment) => {
  if (shipment.sit) return "SIT Railcar";
  return shipment.carrierType === CarrierType.TRUCK
    ? "Trailer Storage"
    : "Railcar Storage";
};

/**
 * Shipment fallback billing: build a shipment-level billable record straight
 * from the InboundShipment, for cars whose Inventory rows produced no storage
 * billing this period — a SIT car's empty tally sheet, RELEASED inventory,
 * null receivedAt, or a tracked car whose stock was depleted before the period
 * while the car sat on the track into it.
 *
 * Only shipment-level (carrierType) rules bill here (see resolvePerCarRule);
 * a shipment with no such rule is skipped rather than guessed at. Like any
 * per-vehicle line, a departed car bills through the day it left the track.
 */
const buildShipmentBillableRecord = (shipment, storageRules, options) => {
  if (!shipment?.id || !shipment?.placedDateTime) return null;

  const rule = resolvePerCarRule(shipment, storageRules);
  if (!rule) return null;

  // Departure gates (released without a date, left before the period) live in
  // buildShipmentLevelStorageBillableItem, shared with tracked inventory; the
  // date itself is also needed here to shape the synthetic record.
  const { departureDateTime } = getShipmentDeparture(shipment);

  // Inventory-shaped view of the car so rule matching (doesRuleApply) and the
  // shared per-vehicle builder read the same fields they do for real
  // inventory records.
  const syntheticInventory = {
    id: shipment.id,
    inboundShipmentId: shipment.id,
    inboundShipment: shipment,
    carrierType: shipment.carrierType,
    carrierEquipmentTypeId: shipment.carrierEquipmentTypeId ?? null,
    areaId: shipment.areaId ?? null,
    zoneId: shipment.zoneId ?? null,
    reloadYardCommodityId: shipment.reloadYardCommodityId ?? null,
    receivedAt: shipment.placedDateTime,
    // A collected car is gone whether or not its status caught up, so the
    // departure date — not the status — decides how the summary reads it.
    status: departureDateTime
      ? InventoryStatus.SHIPPED
      : InventoryStatus.STORED,
    depletedAt: departureDateTime,
    weight: 0,
    weightUOM: null,
  };

  const billableItem = buildShipmentLevelStorageBillableItem(
    syntheticInventory,
    rule,
    {
      startDate: options.startDate,
      endDate: options.endDate,
      freeSlotOpensAt: options.freeSlotOpensById?.get(shipment.id),
    },
  );
  if (!billableItem) return null;

  // The synthetic inventory carries the status, placement and release the
  // summary reads, so the car's day columns are computed the same way a
  // tracked car's are rather than inline with their own meaning.
  syntheticInventory.billableItems = [billableItem];
  computeInventorySummary(syntheticInventory, options);

  return {
    id: `${shipment.id}-${options.startDate}-${options.endDate}`,
    billingSource: BillingSource.SIT_SHIPMENT,
    inboundShipmentId: shipment.id,
    receivedAt: shipment.placedDateTime,
    depletedAt: departureDateTime,
    productCode: "",
    productName: vehicleStorageLabel(shipment),
    poNumber: shipment.poNumber || "",
    railCarNumber: shipment.railCarNumber || "",
    inventoryUOM: rule.billingUOM,
    weight: 0,
    weightUOM: null,
    areaId: shipment.areaId ?? null,
    zoneId: shipment.zoneId ?? null,
    billableItems: [billableItem],
    totalFees: 0, // set by accumulateRunningTotals
    totalDaysStored: syntheticInventory.totalDaysStored,
    billableDaysTotal: syntheticInventory.billableDaysTotal,
    billableUom: syntheticInventory.billableUom || null,
  };
};

// #########################################################
//        STORAGE BILLING: MAIN ORCHESTRATOR
// #########################################################
export const processInventoryStorageReportData = ({
  inventories,
  leasedZones = [],
  storageShipments = [],
  storageRules,
  options: reportOptions = {},
  setFinalData,
  setUnitTotals,
  setHasWeight,
}) => {
  const runningTotalFees = {};
  let hasWeight = false;
  const billableInventories = [];
  const billedShipments = new Set();
  // Storage inside a leased zone is billed once, as the zone's flat area
  // charge. trimLeasedZoneInventories has already dropped those inventories
  // before the report gets here, so neither dedup set below ever sees a car
  // parked in one — the per-car shipment fallback has to skip them by zone.
  const leasedZoneIds = new Set(
    (leasedZones || []).map((zone) => zone?.id).filter(Boolean),
  );
  // Shipments whose inventories produced inventory-level billable entries:
  // storage for these is billed per inventory, so the per-car shipment fallback
  // must skip them.
  const inventoryRuledShipmentIds = new Set();
  const endBoundary = toBoundaryDate(reportOptions.endDate);

  // Free-car slots are decided across every vehicle in the report before any
  // one of them is billed: which vehicle holds a slot depends on the vehicles
  // placed ahead of it, from either source.
  const options = {
    ...reportOptions,
    freeSlotOpensById: allocateFreeCarSlots({
      inventories,
      storageShipments,
      leasedZoneIds,
      storageRules,
      options: reportOptions,
    }),
  };

  leasedZones.forEach((zone) => {
    const leasedZoneRecord = buildLeasedZoneBillableRecord(
      zone,
      storageRules,
      options,
    );
    if (!leasedZoneRecord) return;

    const leasedBillableItem = leasedZoneRecord.billableItems?.[0];
    if (!leasedBillableItem) return;

    // A leased line the rules could not price is left out of the unit totals
    // here, where every other source marks that UOM with the failure label
    // (MISSING RULE / SERVICE / QTY / RATE) instead. The
    // divergence is deliberate for now: closing it changes a total a customer
    // has already been shown, so it is a billing decision, not a cleanup.
    if (typeof leasedBillableItem.fees !== "string") {
      addLineToRunningTotals(leasedBillableItem, runningTotalFees);
    }

    billableInventories.push(leasedZoneRecord);
  });

  inventories.forEach((inv) => {
    inv.billableItems = [];
    inv.totalFees = 0;
    inv.billableUom = null;

    if (inv.weightUOM && inv.weightUOM !== inv.inventoryUOM) {
      hasWeight = true;
    }

    const { rule, isShipmentLevel } = classifyBillingPath(inv, storageRules);

    if (isShipmentLevel) {
      const items = processShipmentLevelBilling(
        inv,
        rule,
        options,
        billedShipments,
        endBoundary,
      );
      if (items === null) return;
      items.forEach((bi) => {
        inv.billableItems.push(bi);
        accumulateRunningTotals(inv, bi, runningTotalFees);
      });
      // the shipment-level path used to return without a summary, so a car
      // billed per railcar showed blank day columns beside identical cars
      computeInventorySummary(inv, options);
      billableInventories.push(inv);
      return;
    }

    const entries = buildBillableEntries(inv, storageRules, options);

    // Entries decide the suppression, not the header-level rule. Entries are
    // built per split with their own zone- and area-scoped rules, so a header
    // rule can be null while the splits still bill (suppress, or the SIT
    // fallback double-bills the car), and a header rule can match while a
    // zero-quantity row builds no entries (do not suppress, or the car goes
    // unbilled by both paths). Both builders emit only positive-quantity
    // entries, so length alone is the signal. allocateFreeCarSlots decides
    // the same question the same way — keep the two tests together.
    if (inv.inboundShipmentId && entries.length > 0) {
      inventoryRuledShipmentIds.add(inv.inboundShipmentId);
    }

    entries.forEach((entry) => {
      const bi = processStorageEntry(inv, entry, entries, options);
      if (!bi) return;
      inv.billableItems.push(bi);
      accumulateRunningTotals(inv, bi, runningTotalFees);
    });

    computeInventorySummary(inv, options);

    if (inv.billableItems.length > 0) billableInventories.push(inv);
  });

  // Per-car shipment fallback: bill cars whose Inventory rows produced no
  // storage billing this period (empty SIT tally sheet, RELEASED inventory,
  // null receivedAt, stock depleted before the period with the car still on
  // the track).
  //
  // It is a pass of its own, after the inventory pass rather than inside it,
  // because what it bills is defined by what that pass did not: a car is only
  // billed here once every inventory row has had its chance to account for it.
  // So it skips cars parked in a leased zone (leasedZoneIds, billed as flat
  // area), shipments already billed shipment-level through their inventories
  // (billedShipments) and shipments governed by inventory-level rules
  // (inventoryRuledShipmentIds) - tracked inventory never double-bills.
  const shipmentRecords = [];
  (storageShipments || []).forEach((shipment) => {
    if (!shipment?.id) return;
    if (shipment.zoneId && leasedZoneIds.has(shipment.zoneId)) return;
    if (billedShipments.has(shipment.id)) return;
    if (inventoryRuledShipmentIds.has(shipment.id)) return;

    const shipmentRecord = buildShipmentBillableRecord(
      shipment,
      storageRules,
      options,
    );
    if (!shipmentRecord) return;

    billedShipments.add(shipment.id);
    accumulateRunningTotals(
      shipmentRecord,
      shipmentRecord.billableItems[0],
      runningTotalFees,
    );
    shipmentRecords.push(shipmentRecord);
  });
  billableInventories.push(...shipmentRecords);

  // The grid re-sorts client-side by receivedAt, but the CSV export walks this
  // array as it stands. Appending the synthetic rows after every inventory row
  // put a car placed in July below stock received in August on the export
  // alone, so the two are ordered together here, once.
  billableInventories.sort(
    (a, b) =>
      new Date(a.receivedAt) - new Date(b.receivedAt) ||
      String(a.id).localeCompare(String(b.id)),
  );

  setFinalData(billableInventories);
  setUnitTotals(runningTotalFees);
  setHasWeight(hasWeight);
};

/**
 * Create a billable item object
 * @param {Object} inventory - Inventory item
 * @param {number} quantity - Quantity to bill
 * @param {string} shippedDate - Date shipped
 * @param {Object} options - Additional options containing freeDays
 * @returns {Object} Billable item
 */
const createBillableItem = (inventory, quantity, shippedDate, options) => {
  const { startDate, endDate, accrual, freeDays } = options;

  const getStoredWeight = () => {
    if (inventory.weight != null && inventory.weightUOM) {
      return inventory.inventoryUOM === inventory.weightUOM
        ? quantity // weight = inventory.quantityOnHand
        : getConversionValue(
            inventory,
            quantity,
            inventory.weightUOM,
            inventory.inventoryUOM,
            null,
          );
      // : trimDecimal(inventory.weight * quantity, 3); // calculate per quantity
    }
    return 0;
  };

  return {
    quantity,
    inventoryUOM: inventory.inventoryUOM,
    weight: getStoredWeight(),
    weightUOM: inventory.weightUOM,
    dateShipped: shippedDate,
    billableDays: calculateBillableDays(
      startDate,
      endDate,
      inventory.receivedAt,
      shippedDate,
      accrual,
      freeDays,
    ),
  };
};

/**
 * Calculate billable days for storage
 * @param {string} endDate - End date
 * @param {string} receivedDate - Date received
 * @param {string} shippedDate - Date shipped
 * @param {Object} options - Additional options containing freeDays
 * @returns {number} Billable days
 */
export const calculateBillableDays = (
  startDate,
  endDate,
  receivedDate,
  shippedDate,
  accrual = FeeAccrual.FLAT_RATE,
  freeDays = 0,
) => {
  let billableDays = 0;

  const start = toBoundaryDate(startDate);
  const end = toBoundaryDate(endDate);
  const received = toDateSafe(receivedDate);
  const shipped = toDateSafe(shippedDate);

  if (!start || !end || !received) return 0;

  if (accrual === FeeAccrual.FLAT_RATE || accrual === FeeAccrual.FLAT_ONHAND) {
    // fees accrued once per billing period: [start, end)
    billableDays = differenceInDays(end, start);
  } else {
    // prorated fees accrued DAILY
    const firstBillingDay = addDays(
      startOfDay(received),
      Number(freeDays) || 0,
    );
    // An event-driven window starts at the placement instant, not midnight;
    // that day is billed whole like every other day.
    const billingStart = isAfter(firstBillingDay, start)
      ? firstBillingDay
      : startOfDay(start);

    billableDays = differenceInDays(
      getStorageCountThrough(shipped, end, isYMD(endDate)),
      billingStart,
    );
  }

  return billableDays;
};

/**
 * Where a stay's counted days stop (exclusive): midnight after the day it
 * left, or `end` when it was still here then. Storage counts calendar days
 * with both ends included — placed on the 1st and pulled on the 7th is 7
 * days — so the day it arrived and the day it left each count once.
 *
 * `endIsExclusive` says what `end` is. A billing period's end is the next
 * period's first midnight, so a departure at exactly that instant belongs to
 * the next period and stops here at `end`; counting it through the next
 * midnight billed that day in both periods. An event-driven window's end is
 * the departure itself, which always counts, even when someone typed it as
 * 00:00:00 — the clock time never decides.
 */
const getStorageCountThrough = (shipped, end, endIsExclusive) => {
  if (!shipped || isAfter(shipped, end)) return end;
  if (endIsExclusive && !isBefore(shipped, end)) return end;
  return addDays(startOfDay(shipped), 1);
};

/**
 * Calendar days in storage from the day received through the day it left,
 * or through `endDate` when it was still here — the days PRORATED_DAILY bills
 * before free days come off. Free-day gates and the report's day columns
 * read this so they agree with what was billed; counting whole 24-hour
 * periods instead dropped up to two days on every stay.
 *
 * `endDate` is passed raw, not parsed: a period end arrives as yyyy-MM-dd
 * and is exclusive, an event window's end arrives as a timestamp and counts.
 */
const countStorageDays = (receivedAt, leftAt, endDate) => {
  const received = toDateSafe(receivedAt);
  const end = toDateSafe(endDate);
  if (!received || !end) return 0;

  return Math.max(
    0,
    differenceInDays(
      getStorageCountThrough(toDateSafe(leftAt), end, isYMD(endDate)),
      startOfDay(received),
    ),
  );
};

/**
 * Get total fee for inventory
 * @param {Object} inventory - Inventory item
 * @param {Object} billableItem - Billable item
 * @returns {number|string} Total fee
 */
const getTotalStorageFee = (inventory, billableItem) => {
  // A fee carried as text names the lookup that failed (MISSING RULE,
  // MISSING SERVICE, MISSING QTY, MISSING RATE). Keep the first such label
  // as the total so the report says what failed instead of flattening every
  // failure into MISSING RATE.
  if (typeof inventory.totalFees === "string") return inventory.totalFees;
  if (typeof billableItem.fees === "string") return billableItem.fees;

  // Both values should be numbers, add them
  return trimDecimal(inventory.totalFees + billableItem.fees);
};

/**
 * Get sum total for unit totals
 * @param {Object} runningUnitTotals - Running unit totals
 * @param {string} billableUom - Billable unit of measure
 * @param {number|string} lineTotal - Total fees
 * @returns {number|string} Sum total
 */
const getStorageSumTotal = (runningUnitTotals, billableUom, lineTotal) => {
  // A total already carrying a failure label keeps it — first failure wins
  if (typeof runningUnitTotals[billableUom] === "string")
    return runningUnitTotals[billableUom];

  // A line the rules could not price makes this UOM's total its failure label
  if (typeof lineTotal === "string") return lineTotal;

  // Both values are numbers, add them
  const existingTotal = runningUnitTotals[billableUom] || 0;
  return trimDecimal(existingTotal + lineTotal);
};
